import { useEffect, useMemo, useState, type ChangeEvent, type ReactNode } from 'react';
import {
  activarUsuario,
  actualizarParametro,
  eliminarUsuario,
  listarParametros,
  listarUsuarios,
  resetearContrasena,
  type Parametro,
  type UserWithProfile,
} from '../../lib';
import { useAuth } from '../../context/AuthContext';
import type { Conductor, PagoPaquete, Vehiculo, Viaje } from '../../lib/appTypes';
import {
  EMPTY_DASHBOARD_STATS,
  assignViaje,
  createSucursal,
  deleteSucursal,
  deleteVehiculo,
  getAdminDashboardStats,
  getSucursales,
  getConductores,
  getViajeById,
  getVehiculos,
  getViajes,
  getViajesPaginados,
  subscribeStorage,
  uploadFileToMinio,
  updateVehiculo,
  updateSucursal,
  type DashboardStatsData,
} from '../../lib/storage';
import type { Sucursal } from '../../lib/appTypes';
import AdminEditViajeForm from './AdminEditViajeForm';
import FormUsuario from './FormUsuario';
import VincularVendedorErpModal, { textoAvisoVinculoErp } from './VincularVendedorErpModal';
import FormVehiculo from './FormVehiculo';
import MapaAdmin from './MapaAdmin';
import AdminCajaPanel from './AdminCajaPanel';
import BodegaProveedoresPanel from './BodegaProveedoresPanel';
import BodegaInsumosPanel from './BodegaInsumosPanel';
import BodegaAsignacionesPanel from './BodegaAsignacionesPanel';
import MantenimientosPanel from './MantenimientosPanel';
import AdminGasolinaHistoryPanel from './AdminGasolinaHistoryPanel';
import AdminKilometrosPanel from './AdminKilometrosPanel';
import {
  ActivityFeed,
  DashboardStatCard,
  PanelShell,
  SectionSurface,
  WeeklyBarChart,
  type PanelNavItem,
} from '../../components/role-ui';
import { Modal, ModalCloseButton, alertar, avisar, confirmar, mensajeDeError, pedirTexto, SkeletonRows, BarraProgreso } from '../../components/ui-kit';

type Section =
  | 'dashboard'
  | 'personal'
  | 'asignaciones'
  | 'vehiculos'
  | 'sucursales'
  | 'bodega-proveedores'
  | 'bodega-insumos'
  | 'bodega-asignaciones'
  | 'gasolina-historial'
  | 'mantenimientos'
  | 'parametros'
  | 'mapa'
  | 'historial'
  | 'caja'
  | 'reportes-kilometros';

type ResumenAsignacion = {
  viajeNombre: string;
  asignaciones: Array<{
    titulo: string;
    conductorNombre: string;
    vehiculoNombre: string;
  }>;
};

const HISTORIAL_PAGE_SIZE = 10;

const SECTION_COPY: Record<Section, { label: string; icon: string; detail: string }> = {
  dashboard: {
    label: 'Dashboard',
    icon: 'fa-solid fa-chart-line',
    detail: 'Resumen de la operación de hoy, actividad reciente y estado de la flota.',
  },
  personal: {
    label: 'Gestión de Personal',
    icon: 'fa-solid fa-users',
    detail: 'Crea, edita, activa o desactiva usuarios, cambia contraseñas y asigna el rol operativo.',
  },
  asignaciones: {
    label: 'Asignar entregas',
    icon: 'fa-solid fa-clipboard-check',
    detail: 'Filtra las entregas creadas por los vendedores y asígnalas a un conductor respetando pico y placa.',
  },
  vehiculos: {
    label: 'Gestión de Autos',
    icon: 'fa-solid fa-car-side',
    detail: 'Datos, etiquetas y estado de cada vehículo. El conductor se asigna directamente en cada fila.',
  },
  sucursales: {
    label: 'Sucursales',
    icon: 'fa-solid fa-building',
    detail: 'Sucursales que los vendedores eligen al crear entregas: nombre, dirección y teléfono.',
  },
  parametros: {
    label: 'Parámetros',
    icon: 'fa-solid fa-sliders',
    detail: 'Todos los parámetros del sistema, públicos o no. Desde aquí solo se cambia su valor.',
  },
  mapa: {
    label: 'Mapa activo',
    icon: 'fa-solid fa-route',
    detail: 'Elige un conductor para seguir su viaje en curso. El mapa se actualiza cada 10 segundos.',
  },
  historial: {
    label: 'Historial',
    icon: 'fa-solid fa-file-lines',
    detail: 'Todos los viajes con su estado (no iniciado, en curso o finalizado) y filtros por entrega, vendedor, conductor, tipo, fechas y devoluciones.',
  },
  'bodega-proveedores': {
    label: 'Proveedores',
    icon: 'fa-solid fa-truck',
    detail: 'Proveedores de insumos: datos fiscales, forma de pago SRI y si son propios o externos.',
  },
  'bodega-insumos': {
    label: 'Insumos',
    icon: 'fa-solid fa-box',
    detail: 'Insumos con precio referencial, stock disponible y proveedor asociado.',
  },
  'bodega-asignaciones': {
    label: 'Asignar insumos a vehículos para mantenimiento interno',
    icon: 'fa-solid fa-clipboard-list',
    detail: 'Presupuesto asignado por vehículo, proveedor de cada insumo y valor cargado.',
  },
  'gasolina-historial': {
    label: 'Historial de Gasolina',
    icon: 'fa-solid fa-gas-pump',
    detail: 'Cargas de gasolina registradas por los conductores. Filtra por placa y rango de fechas.',
  },
  mantenimientos: {
    label: 'Mantenimientos externos',
    icon: 'fa-solid fa-screwdriver-wrench',
    detail: 'Mantenimientos por vehículo: fechas de ingreso y salida, kilometraje, descripción y responsable.',
  },
  caja: {
    label: 'Caja',
    icon: 'fa-solid fa-cash-register',
    detail: 'Abre cajas, registra los envíos pagados a las compañías de transporte y exporta el reporte.',
  },
  'reportes-kilometros': {
    label: 'Kilómetros',
    icon: 'fa-solid fa-gauge-high',
    detail: 'Kilómetros por vehículo y conductor en un rango de fechas, con exportación a Excel o PDF.',
  },
};

function normalizeRole(user: UserWithProfile): string {
  return (user.profile?.rol || 'USUARIO').toUpperCase();
}

/** Filtro de personal: vendedores sincronizados desde el ERP. */
const ROL_FILTRO_ERP = 'ERP';

/** Vendedor sincronizado desde el ERP: sin acceso al panel, se administra en el ERP. */
function esUsuarioErp(user: UserWithProfile): boolean {
  return user.profile?.origen_externo === 'ERP';
}

type FiltroVehiculo = 'todos' | 'disponibles' | 'asignados' | 'novedad' | 'pico_y_placa';

const VEHICULO_FILTROS: Array<{ valor: FiltroVehiculo; etiqueta: string; punto: string; cumple: (vehiculo: Vehiculo) => boolean }> = [
  { valor: 'todos', etiqueta: 'Todos', punto: 'bg-stone-400', cumple: () => true },
  { valor: 'disponibles', etiqueta: 'Disponibles', punto: 'bg-emerald-500', cumple: vehiculo => !vehiculo.etiqueta && vehiculo.disponible },
  { valor: 'asignados', etiqueta: 'Con conductor', punto: 'bg-blue-500', cumple: vehiculo => Boolean(vehiculo.conductor) },
  { valor: 'novedad', etiqueta: 'Con novedad', punto: 'bg-amber-500', cumple: vehiculo => Boolean(vehiculo.etiqueta) },
  { valor: 'pico_y_placa', etiqueta: 'Pico y placa', punto: 'bg-sky-500', cumple: vehiculo => !vehiculo.etiqueta && !vehiculo.disponible && vehiculo.motivoNoDisponible === 'pico_y_placa' },
];

/** Texto y colores de la etiqueta de estado de un vehículo (misma lógica que las tarjetas anteriores). */
function estadoVehiculo(vehiculo: Vehiculo): { texto: string; clases: string } {
  if (vehiculo.etiqueta) return { texto: etiquetaLabel(vehiculo.etiqueta), clases: 'border-amber-200 bg-amber-50 text-amber-800' };
  if (vehiculo.disponible) return { texto: 'Disponible', clases: 'border-emerald-200 bg-emerald-50 text-emerald-700' };
  if (vehiculo.motivoNoDisponible === 'pico_y_placa') return { texto: 'Pico y placa', clases: 'border-sky-200 bg-sky-50 text-sky-700' };
  return { texto: 'Asignado', clases: 'border-blue-200 bg-blue-50 text-blue-700' };
}

const PERSONAL_ROLES = [
  { valor: 'todos', etiqueta: 'Todos' },
  { valor: 'CONDUCTOR', etiqueta: 'Conductores' },
  { valor: 'VENDEDOR', etiqueta: 'Vendedores' },
  { valor: ROL_FILTRO_ERP, etiqueta: 'Vendedores ERP' },
  { valor: 'ADMINISTRADOR', etiqueta: 'Administradores' },
];

function roleBadgeClasses(role: string): string {
  if (role === 'CONDUCTOR') return 'bg-teal-100 text-teal-800 border border-teal-200';
  if (role === 'VENDEDOR') return 'bg-amber-100 text-amber-900 border border-amber-200';
  if (role === 'ADMINISTRADOR') return 'bg-stone-900 text-white border border-stone-900';
  return 'bg-stone-100 text-stone-600 border border-stone-200';
}

function etiquetaLabel(etiqueta: Vehiculo['etiqueta']): string {
  if (!etiqueta) return 'Sin etiqueta';
  if (etiqueta === 'mantenimiento') return 'Mantenimiento';
  if (etiqueta === 'averia') return 'Avería';
  return 'Taller';
}

function estadoViajeLabel(estado?: Viaje['estado']): string {
  if (estado === 'finalizado') return 'Finalizado';
  if (estado === 'en_curso') return 'En curso';
  if (estado === 'pendiente') return 'No iniciado';
  return '—';
}

function estadoViajeClases(estado?: Viaje['estado']): string {
  if (estado === 'finalizado') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (estado === 'en_curso') return 'border-blue-200 bg-blue-50 text-blue-700';
  return 'border-amber-200 bg-amber-50 text-amber-800';
}

function tipoEntregaLabel(tipo?: Viaje['tipoEntrega']): string {
  if (tipo === 'entrega') return 'Entrega';
  if (tipo === 'envio') return 'Envío';
  if (tipo === 'transferencia') return 'Transferencia';
  return '—';
}

function tipoGasolinaLabel(tipo?: Vehiculo['tipoGasolina'] | string): string {
  if (tipo === 'extra') return 'Extra';
  if (tipo === 'super') return 'Súper';
  if (tipo === 'diesel') return 'Diésel';
  return '—';
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('es-EC', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

function getViajeFinalLocationLabel(viaje: Viaje): string {
  if (viaje.tipoEntrega === 'transferencia') {
    return viaje.transferencia?.bodegaDestino?.trim() || '—';
  }

  return viaje.paquete?.cliente?.direccion?.trim() || '—';
}

function getViajeDestinationUrl(viaje: Viaje): string | undefined {
  const url = viaje.urlDestino?.trim();
  return url ? url : undefined;
}

function getTripTotal(viaje?: Viaje): number | undefined {
  if (!viaje) return undefined;
  if (typeof viaje.totalViaje === 'number' && !Number.isNaN(viaje.totalViaje)) {
    return viaje.totalViaje;
  }

  return undefined;
}

function parseParametroNumber(value?: string | null): number | undefined {
  if (!value?.trim()) return undefined;
  const parsed = Number(value.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : undefined;
}

function getTripBaseCost(viaje?: Viaje, tarifaPorKilometro?: number): number | undefined {
  if (typeof tarifaPorKilometro !== 'number' || Number.isNaN(tarifaPorKilometro)) return undefined;
  if (typeof viaje?.kilometrosRecorridos !== 'number' || Number.isNaN(viaje.kilometrosRecorridos)) return undefined;
  return Number((viaje.kilometrosRecorridos * tarifaPorKilometro).toFixed(2));
}

function formatPaymentLabel(tipo?: PagoPaquete['tipo'] | string): string {
  if (tipo === 'efectivo') return 'Efectivo';
  if (tipo === 'transferencia') return 'Transferencia';
  if (tipo === 'cheque') return 'Cheque';
  if (tipo === 'parcial') return 'Por cobrar';
  if (tipo === 'nota_credito') return 'Nota de crédito';
  if (tipo === 'completo') return 'Pagado';
  return '—';
}

/** Monto que el conductor debía cobrar según la forma de pago acordada. */
function getAmountToCollect(pago: PagoPaquete | null | undefined, total: number | undefined): number | undefined {
  if (!pago || pago.tipo === 'completo') return 0;
  if (pago.tipo === 'parcial') {
    if (typeof total !== 'number') return undefined;
    return Math.max(Number((total - (pago.montoPagado ?? 0)).toFixed(2)), 0);
  }
  if (pago.tipo === 'nota_credito') return pago.valorNotaCredito;
  return typeof pago.monto === 'number' ? pago.monto : total;
}

function getGuiasDetalle(viaje: Viaje): { title: string; guias: Array<{ url: string; nombreArchivo?: string; numeroFactura?: string; direccion?: string }> } {
  const firmadas = (viaje.guiasFirmadas ?? []).filter(guia => guia.url);
  if (firmadas.length > 0) {
    return { title: 'Guías firmadas', guias: firmadas };
  }

  const generadas = (viaje.paquete?.guiasPdf ?? []).filter(guia => guia.url);
  if (generadas.length > 0) {
    return { title: 'Guías generadas', guias: generadas };
  }

  if (viaje.paquete?.guiaPdfUrl) {
    return {
      title: 'Guías generadas',
      guias: [{ url: viaje.paquete.guiaPdfUrl, nombreArchivo: viaje.paquete.guiaPdfNombreArchivo }],
    };
  }

  return { title: 'Guías', guias: [] };
}

function getGuiaPrincipalDocumento(viaje?: Viaje | null): { url: string; nombreArchivo: string } | null {
  const url = viaje?.paquete?.guiaPdfUrl?.trim();
  if (!url) return null;

  return {
    url,
    nombreArchivo: viaje?.paquete?.guiaPdfNombreArchivo?.trim() || 'Guía generada',
  };
}

function getEnvioDocumento(viaje?: Viaje | null): { url: string; nombreArchivo: string } | null {
  const url = viaje?.paquete?.envioPdfUrl?.trim();
  if (!url) return null;

  return {
    url,
    nombreArchivo: viaje?.paquete?.envioPdfNombreArchivo?.trim() || 'PDF de envío',
  };
}

function extractInvoiceNumberFromGuideName(nombreArchivo?: string): string | undefined {
  const normalizedName = nombreArchivo?.trim();
  if (!normalizedName) return undefined;

  const withoutExtension = normalizedName.replace(/\.pdf$/i, '').trim();
  const facturaMatch = withoutExtension.match(/^factura[\s:_-]+(.+)$/i);
  return facturaMatch?.[1]?.trim() || undefined;
}

function getViajeInvoiceNumbers(viaje: Viaje): string[] {
  const guiaNumbers = getGuiasDetalle(viaje).guias
    .map(guia => guia.numeroFactura?.trim() || extractInvoiceNumberFromGuideName(guia.nombreArchivo))
    .filter((numero): numero is string => Boolean(numero));

  if (guiaNumbers.length > 0) return guiaNumbers;

  const clienteNumero = viaje.paquete?.cliente?.numero?.trim();
  return clienteNumero ? [clienteNumero] : [];
}

function countOpenTripsForConductor(conductorId: string, pendingTrips: Viaje[], activeTrips: Viaje[]): number {
  return [...pendingTrips, ...activeTrips].filter(viaje => viaje.conductorId === conductorId && viaje.estado !== 'finalizado').length;
}

function formatShortDate(timestamp?: number): string {
  if (!timestamp) return 'Ahora';
  return new Intl.DateTimeFormat('es-EC', {
    day: '2-digit',
    month: 'short',
  }).format(new Date(timestamp));
}

function formatShortDateTime(timestamp?: number): string {
  if (!timestamp) return 'Ahora';
  return new Intl.DateTimeFormat('es-EC', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(timestamp));
}

function sameDay(timestampA: number, timestampB: number): boolean {
  const left = new Date(timestampA);
  const right = new Date(timestampB);
  return left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate();
}

function getVehiculoDashboardStatus(vehiculo: Vehiculo): {
  label: string;
  className: string;
  dotClassName: string;
} {
  if (vehiculo.etiqueta === 'mantenimiento') {
    return {
      label: 'Mantenimiento',
      className: 'bg-orange-50 text-orange-700',
      dotClassName: 'bg-orange-500',
    };
  }

  if (vehiculo.etiqueta === 'averia') {
    return {
      label: 'Avería',
      className: 'bg-red-50 text-red-700',
      dotClassName: 'bg-red-600',
    };
  }

  if (vehiculo.motivoNoDisponible === 'pico_y_placa') {
    return {
      label: 'Pico y placa',
      className: 'bg-sky-50 text-sky-700',
      dotClassName: 'bg-sky-500',
    };
  }

  if (vehiculo.disponible && !vehiculo.conductor) {
    return {
      label: 'Disponible',
      className: 'bg-emerald-50 text-emerald-700',
      dotClassName: 'bg-emerald-500',
    };
  }

  return {
    label: 'Pendiente',
    className: 'bg-amber-50 text-amber-700',
    dotClassName: 'bg-amber-500',
  };
}

function getVehiculoAvailabilityText(vehiculo: Vehiculo): string {
  if (vehiculo.motivoNoDisponible === 'pico_y_placa') {
    return 'Alerta vigente por pico y placa';
  }
  if (vehiculo.motivoNoDisponible === 'asignado') {
    return 'Actualmente asignado a un conductor';
  }
  if (vehiculo.motivoNoDisponible === 'etiqueta') {
    return 'Bloqueado por novedad o mantenimiento';
  }
  return 'Listo para asignar';
}

function formatConductorOptionLabel(conductor: Conductor, openTripCountsByConductor: Map<string, number>): string {
  const totalAsignados = openTripCountsByConductor.get(conductor.id) ?? 0;
  const totalVehiculos = conductor.vehiculos?.length ?? 0;
  return `${conductor.nombre} · ${totalVehiculos} vehiculo${totalVehiculos === 1 ? '' : 's'} · ${totalAsignados} viaje${totalAsignados === 1 ? '' : 's'}`;
}

function formatVehiculoOptionLabel(vehiculo: Vehiculo): string {
  return `${vehiculo.placa} · ${vehiculo.marca} ${vehiculo.modelo}${vehiculo.etiqueta ? ' · no disponible' : vehiculo.picoYPlacaDetalle?.activo ? ' · pico y placa' : ''}`;
}

function formatVehiculoResumen(vehiculo?: Vehiculo | null): string {
  if (!vehiculo) return 'Sin vehículo asignado';
  return [vehiculo.placa, `${vehiculo.marca} ${vehiculo.modelo}`.trim()].filter(Boolean).join(' · ');
}

type PicoYPlacaRestriction = NonNullable<Vehiculo['picoYPlacaDetalle']> | {
  activo?: boolean;
  dia?: string;
  horario?: string;
  horaQuito?: string;
  hora_quito?: string;
  placa?: string;
  marca?: string;
  modelo?: string;
  mensaje?: string;
};

function formatPicoYPlacaMessage(restriction?: PicoYPlacaRestriction | null, fallback?: string): string {
  if (!restriction) return fallback ?? 'Advertencia de pico y placa para esta asignación.';

  const horaQuito = restriction.horaQuito ?? (restriction as { hora_quito?: string }).hora_quito;
  const vehiculo = [restriction.placa, restriction.marca, restriction.modelo].filter(Boolean).join(' · ');

  return [
    restriction.mensaje || fallback || 'Advertencia de pico y placa para esta asignación.',
    restriction.dia ? `Día: ${restriction.dia}` : '',
    restriction.horario ? `Horario: ${restriction.horario}` : '',
    horaQuito ? `Hora Quito: ${horaQuito}` : '',
    vehiculo ? `Vehículo: ${vehiculo}` : '',
  ].filter(Boolean).join('\n');
}

function extractValidationMessage(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;

  const payload = data as Record<string, unknown>;
  if (typeof payload.message === 'string' && payload.message.trim()) return payload.message;
  if (typeof payload.reason === 'string' && payload.reason.trim()) return payload.reason;

  for (const value of Object.values(payload)) {
    if (typeof value === 'string' && value.trim()) return value;
    if (Array.isArray(value)) {
      const firstText = value.find(item => typeof item === 'string' && item.trim());
      if (typeof firstText === 'string') return firstText;
    }
  }

  return undefined;
}

function extractPicoYPlacaError(error: unknown): { message: string; restriction: PicoYPlacaRestriction | null } {
  const apiError = error as {
    message?: string;
    restriction?: PicoYPlacaRestriction;
    data?: Record<string, unknown> & { restriction?: PicoYPlacaRestriction };
  };
  const validationMessage = extractValidationMessage(apiError.data);

  return {
    message: validationMessage ?? apiError.message ?? 'No se pudo completar la asignación.',
    restriction: apiError.data?.restriction ?? apiError.restriction ?? null,
  };
}

function AdminFormPopup({
  children,
  onClose,
  maxWidthClassName = 'max-w-3xl',
}: {
  children: ReactNode;
  onClose: () => void;
  maxWidthClassName?: string;
}) {
  return (
    <Modal onClose={onClose} maxWidthClassName={maxWidthClassName}>
      <div className="px-6 pb-6 pt-14">{children}</div>
    </Modal>
  );
}

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const [section, setSection] = useState<Section>('dashboard');
  const [parametros, setParametros] = useState<Parametro[]>([]);
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [conductores, setConductores] = useState<Conductor[]>([]);
  const [vehiculos, setVehiculos] = useState<Vehiculo[]>([]);
  const [usuarios, setUsuarios] = useState<UserWithProfile[]>([]);
  const [pendingViajes, setPendingViajes] = useState<Viaje[]>([]);
  const [viajes, setViajes] = useState<Viaje[]>([]);
  const [historialViajes, setHistorialViajes] = useState<Viaje[]>([]);
  const [historialPagina, setHistorialPagina] = useState(1);
  const [historialBusqueda, setHistorialBusqueda] = useState('');
  const [historialTipoEntrega, setHistorialTipoEntrega] = useState<'todas' | 'entrega' | 'envio' | 'transferencia'>('todas');
  const [historialDevolucionFiltro, setHistorialDevolucionFiltro] = useState<'todas' | 'si' | 'no'>('todas');
  const [historialEstadoFiltro, setHistorialEstadoFiltro] = useState<'todos' | 'pendiente' | 'en_curso' | 'completado'>('todos');
  const [historialVendedorFiltro, setHistorialVendedorFiltro] = useState('todos');
  const [historialConductorFiltro, setHistorialConductorFiltro] = useState('todos');
  const [historialFechaInicio, setHistorialFechaInicio] = useState('');
  const [historialFechaFin, setHistorialFechaFin] = useState('');
  const [historialTotalRegistros, setHistorialTotalRegistros] = useState(0);
  const [historialCargando, setHistorialCargando] = useState(false);
  const [mapaConductorId, setMapaConductorId] = useState('');
  const [mapaViaje, setMapaViaje] = useState<Viaje | null>(null);
  const [mapaCargando, setMapaCargando] = useState(false);
  const [mapaError, setMapaError] = useState('');
  const [dataError, setDataError] = useState('');
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState<UserWithProfile | null>(null);
  const [vinculandoErp, setVinculandoErp] = useState<UserWithProfile | null>(null);
  const [showVehiculoForm, setShowVehiculoForm] = useState(false);
  const [editingVehiculo, setEditingVehiculo] = useState<Vehiculo | null>(null);
  const [editingViaje, setEditingViaje] = useState<Viaje | null>(null);
  const [vendedorFiltro, setVendedorFiltro] = useState('todos');
  const [numeroFacturaFiltro, setNumeroFacturaFiltro] = useState('');
  const [tipoEntregaAsignacionFiltro, setTipoEntregaAsignacionFiltro] = useState('todas');
  const [conductorAsignacionFiltro, setConductorAsignacionFiltro] = useState('todos');
  const [seleccionConductorPorViaje, setSeleccionConductorPorViaje] = useState<Record<string, string>>({});
  const [seleccionVehiculoPorViaje, setSeleccionVehiculoPorViaje] = useState<Record<string, string>>({});
  const [seleccionConductorSecundarioPorViaje, setSeleccionConductorSecundarioPorViaje] = useState<Record<string, string>>({});
  const [seleccionVehiculoSecundarioPorViaje, setSeleccionVehiculoSecundarioPorViaje] = useState<Record<string, string>>({});
  const [seleccionConductorPorVehiculo, setSeleccionConductorPorVehiculo] = useState<Record<string, string>>({});
  const [asignandoViajeId, setAsignandoViajeId] = useState<string | null>(null);
  const [cargandoEdicionViajeId, setCargandoEdicionViajeId] = useState<string | null>(null);
  const [asignandoVehiculoId, setAsignandoVehiculoId] = useState<string | null>(null);
  const [parametrosCargando, setParametrosCargando] = useState(false);
  const [parametrosBusqueda, setParametrosBusqueda] = useState('');
  const [editingParametro, setEditingParametro] = useState<Parametro | null>(null);
  const [parametroValor, setParametroValor] = useState('');
  const [savingParametro, setSavingParametro] = useState(false);
  const [uploadingParametroArchivo, setUploadingParametroArchivo] = useState(false);
  const [detalleViaje, setDetalleViaje] = useState<Viaje | null>(null);
  const [historialMapaViaje, setHistorialMapaViaje] = useState<Viaje | null>(null);
  const [resumenAsignacion, setResumenAsignacion] = useState<ResumenAsignacion | null>(null);
  const [viajeEnAsignacion, setViajeEnAsignacion] = useState<Viaje | null>(null);
  const [showSucursalForm, setShowSucursalForm] = useState(false);
  const [editingSucursal, setEditingSucursal] = useState<Sucursal | null>(null);
  const [savingSucursal, setSavingSucursal] = useState(false);
  const [dashboardStats, setDashboardStats] = useState<DashboardStatsData>(EMPTY_DASHBOARD_STATS);
  const [personalBusqueda, setPersonalBusqueda] = useState('');
  const [vehiculoBusqueda, setVehiculoBusqueda] = useState('');
  const [vehiculoFiltroEstado, setVehiculoFiltroEstado] = useState<FiltroVehiculo>('todos');
  const [sucursalBusqueda, setSucursalBusqueda] = useState('');
  const [personalRol, setPersonalRol] = useState('todos');
  const [personalEstado, setPersonalEstado] = useState<'todos' | 'activos' | 'inactivos'>('todos');
  const [sucursalForm, setSucursalForm] = useState({
    nombre: '',
    direccion: '',
    telefono: '',
  });

  async function refreshParametros() {
    try {
      setParametrosCargando(true);
      const response = await listarParametros({ page_size: 500, order_by: 'nombre', order_up: true });
      setParametros(response.data.data);
      setDataError('');
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'No se pudieron cargar los parámetros.');
    } finally {
      setParametrosCargando(false);
    }
  }

  async function refreshSucursales() {
    try {
      const response = await getSucursales();
      setSucursales(response);
      setDataError('');
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'No se pudieron cargar las sucursales.');
    }
  }

  async function refresh() {
    try {
      const [nextUsuarios, nextConductores, nextVehiculos, nextPendingViajes, nextViajes, nextHistorialResumen, nextParametros, nextSucursales, nextDashboardStats] = await Promise.all([
        listarUsuarios({ page_size: 200, order_by: 'id', order_up: true }),
        getConductores(),
        getVehiculos(),
        getViajes({ estado: 'pendiente' }),
        getViajes({ estado: 'en_curso' }),
        getViajesPaginados({ estado: 'completado', page: 1, page_size: 1 }),
        listarParametros({ page_size: 500, order_by: 'nombre', order_up: true }),
        getSucursales(),
        getAdminDashboardStats(),
      ]);

      setUsuarios(nextUsuarios.data.data);
      setConductores(nextConductores);
      setVehiculos(nextVehiculos);
      setPendingViajes(nextPendingViajes);
      setViajes(nextViajes);
      setHistorialTotalRegistros(nextHistorialResumen.count);
      setParametros(nextParametros.data.data);
      setSucursales(nextSucursales);
      setDashboardStats(nextDashboardStats);
      setDataError('');

      return {
        conductores: nextConductores,
        pendingViajes: nextPendingViajes,
        viajes: nextViajes,
      };
    } catch (err: unknown) {
      setDataError(
        (err as { message?: string })?.message ?? 'No se pudieron sincronizar los datos del panel administrativo.',
      );

      return undefined;
    }
  }

  async function refreshHistorial(
    page = historialPagina,
    search = historialBusqueda,
    tipoEntrega = historialTipoEntrega,
    devolucion = historialDevolucionFiltro,
    vendedorId = historialVendedorFiltro,
    conductorId = historialConductorFiltro,
    fechaInicio = historialFechaInicio,
    fechaFin = historialFechaFin,
  ) {
    try {
      setHistorialCargando(true);
      const result = await getViajesPaginados({
        estado: historialEstadoFiltro !== 'todos' ? historialEstadoFiltro : undefined,
        page,
        page_size: HISTORIAL_PAGE_SIZE,
        search: search.trim() || undefined,
        tipo_entrega: tipoEntrega !== 'todas' ? tipoEntrega : undefined,
        es_devolucion: devolucion === 'todas' ? undefined : devolucion === 'si',
        vendedor: vendedorId !== 'todos' ? Number(vendedorId) : undefined,
        conductor_principal: conductorId !== 'todos' ? Number(conductorId) : undefined,
        fecha_inicio_desde: fechaInicio || undefined,
        fecha_fin_hasta: fechaFin || undefined,
      });
      setHistorialViajes(result.items);
      setHistorialTotalRegistros(result.count);
      setDataError('');
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'No se pudo cargar el historial.');
    } finally {
      setHistorialCargando(false);
    }
  }

  useEffect(() => {
    void refresh();
    const unsubscribe = subscribeStorage(() => {
      void refresh();
    });

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (section !== 'historial') return;

    void refreshHistorial();
  }, [section, historialBusqueda, historialTipoEntrega, historialDevolucionFiltro, historialEstadoFiltro, historialVendedorFiltro, historialConductorFiltro, historialFechaInicio, historialFechaFin, historialPagina]);

  useEffect(() => {
    setHistorialPagina(1);
  }, [historialBusqueda, historialTipoEntrega, historialDevolucionFiltro, historialEstadoFiltro, historialVendedorFiltro, historialConductorFiltro, historialFechaInicio, historialFechaFin]);

  const viajesActivos = viajes.filter(viaje => viaje.estado === 'en_curso');
  const conductorDetalle = detalleViaje
    ? conductores.find(conductor => conductor.id === detalleViaje.conductorId)
    : undefined;
  const detalleGuias = detalleViaje ? getGuiasDetalle(detalleViaje) : { title: 'Guías', guias: [] };
  const detalleGuiaPrincipal = getGuiaPrincipalDocumento(detalleViaje);
  const detalleEnvioDocumento = getEnvioDocumento(detalleViaje);
  const mapaConductor = mapaConductorId
    ? conductores.find(conductor => conductor.id === mapaConductorId)
    : undefined;
  const conductoresActivos = useMemo(() => {
    return conductores.filter(conductor => conductor.activo);
  }, [conductores]);
  const userIdsWithConductorProfile = useMemo(() => {
    return new Set(conductores.map(conductor => conductor.userId).filter(Boolean));
  }, [conductores]);

  const conteoPorRol = useMemo(() => {
    const conteo = new Map<string, number>();
    usuarios.forEach(usuario => {
      const rol = normalizeRole(usuario);
      conteo.set(rol, (conteo.get(rol) ?? 0) + 1);
      if (esUsuarioErp(usuario)) conteo.set(ROL_FILTRO_ERP, (conteo.get(ROL_FILTRO_ERP) ?? 0) + 1);
    });
    return conteo;
  }, [usuarios]);

  const personalFiltrado = useMemo(() => {
    const texto = personalBusqueda.trim().toLowerCase();
    return usuarios.filter(usuario => {
      if (personalRol === ROL_FILTRO_ERP) {
        if (!esUsuarioErp(usuario)) return false;
      } else if (personalRol !== 'todos' && normalizeRole(usuario) !== personalRol) return false;
      const activo = usuario.profile?.estado !== false;
      if (personalEstado === 'activos' && !activo) return false;
      if (personalEstado === 'inactivos' && activo) return false;
      if (!texto) return true;
      return [usuario.username, usuario.email, usuario.profile?.razon_social, usuario.profile?.identificacion]
        .some(valor => String(valor ?? '').toLowerCase().includes(texto));
    });
  }, [usuarios, personalBusqueda, personalRol, personalEstado]);

  const conteoVehiculos = useMemo(() => {
    const conteo: Record<FiltroVehiculo, number> = { todos: vehiculos.length, disponibles: 0, asignados: 0, novedad: 0, pico_y_placa: 0 };
    vehiculos.forEach(vehiculo => {
      VEHICULO_FILTROS.forEach(opcion => {
        if (opcion.valor !== 'todos' && opcion.cumple(vehiculo)) conteo[opcion.valor] += 1;
      });
    });
    return conteo;
  }, [vehiculos]);

  const vehiculosFiltrados = useMemo(() => {
    const texto = vehiculoBusqueda.trim().toLowerCase();
    const filtro = VEHICULO_FILTROS.find(opcion => opcion.valor === vehiculoFiltroEstado);
    return vehiculos.filter(vehiculo => {
      if (filtro && !filtro.cumple(vehiculo)) return false;
      if (!texto) return true;
      return [vehiculo.placa, vehiculo.marca, vehiculo.modelo, vehiculo.anio, vehiculo.conductor?.nombre]
        .some(valor => String(valor ?? '').toLowerCase().includes(texto));
    });
  }, [vehiculos, vehiculoBusqueda, vehiculoFiltroEstado]);

  const sucursalesFiltradas = useMemo(() => {
    const texto = sucursalBusqueda.trim().toLowerCase();
    if (!texto) return sucursales;
    return sucursales.filter(sucursal =>
      [sucursal.nombre, sucursal.direccion, sucursal.telefono].some(valor => String(valor ?? '').toLowerCase().includes(texto)),
    );
  }, [sucursales, sucursalBusqueda]);

  const tarifaPorKilometro = useMemo(() => {
    const parametro = parametros.find(item => item.slug === 'tarifa_por_kilometro');
    return parseParametroNumber(parametro?.valor);
  }, [parametros]);

  useEffect(() => {
    if (section !== 'mapa' || !mapaConductorId) return;

    let cancelled = false;
    const run = async () => {
      try {
        if (!cancelled) {
          setMapaCargando(true);
        }

        const nextViajes = await getViajes({
          conductor: Number(mapaConductorId),
          estado: 'en_curso',
        });

        if (cancelled) return;

        const [activeViaje] = [...nextViajes].sort((left, right) => {
          const leftDate = left.fechaInicio ?? left.fechaFin ?? 0;
          const rightDate = right.fechaInicio ?? right.fechaFin ?? 0;
          return rightDate - leftDate;
        });

        if (!activeViaje) {
          setMapaViaje(null);
          setMapaError('');
          return;
        }

        const detailedViaje = await getViajeById(activeViaje.id) ?? activeViaje;

        if (cancelled) return;

        setMapaViaje(detailedViaje);
        setMapaError('');
      } catch (err: unknown) {
        if (cancelled) return;
        setMapaError((err as { message?: string })?.message ?? 'No se pudo actualizar el mapa activo.');
      } finally {
        if (!cancelled) {
          setMapaCargando(false);
        }
      }
    };

    void run();
    const interval = setInterval(() => {
      void run();
    }, 10000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [section, mapaConductorId]);

  const navItems: PanelNavItem[] = [
    { key: 'dashboard', label: SECTION_COPY.dashboard.label, icon: SECTION_COPY.dashboard.icon },
    { key: 'personal', label: SECTION_COPY.personal.label, icon: SECTION_COPY.personal.icon },
    { key: 'asignaciones', label: SECTION_COPY.asignaciones.label, icon: SECTION_COPY.asignaciones.icon, count: pendingViajes.length || undefined },
    { key: 'vehiculos', label: SECTION_COPY.vehiculos.label, icon: SECTION_COPY.vehiculos.icon },
    { key: 'sucursales', label: SECTION_COPY.sucursales.label, icon: SECTION_COPY.sucursales.icon },
    {
      key: 'bodega',
      label: 'Bodega / Insumos',
      icon: 'fa-solid fa-boxes-stacked',
      children: [
        { key: 'bodega-proveedores', label: SECTION_COPY['bodega-proveedores'].label, icon: SECTION_COPY['bodega-proveedores'].icon },
        { key: 'bodega-insumos', label: SECTION_COPY['bodega-insumos'].label, icon: SECTION_COPY['bodega-insumos'].icon },
        { key: 'bodega-asignaciones', label: SECTION_COPY['bodega-asignaciones'].label, icon: SECTION_COPY['bodega-asignaciones'].icon },
      ],
    },
    { key: 'gasolina-historial', label: SECTION_COPY['gasolina-historial'].label, icon: SECTION_COPY['gasolina-historial'].icon },
    { key: 'mantenimientos', label: SECTION_COPY.mantenimientos.label, icon: SECTION_COPY.mantenimientos.icon },
    { key: 'parametros', label: SECTION_COPY.parametros.label, icon: SECTION_COPY.parametros.icon },
    { key: 'caja', label: SECTION_COPY.caja.label, icon: SECTION_COPY.caja.icon },
    {
      key: 'reportes',
      label: 'Reportes',
      icon: 'fa-solid fa-chart-bar',
      children: [
        { key: 'reportes-kilometros', label: SECTION_COPY['reportes-kilometros'].label, icon: SECTION_COPY['reportes-kilometros'].icon },
      ],
    },
    { key: 'mapa', label: SECTION_COPY.mapa.label, icon: SECTION_COPY.mapa.icon, count: viajesActivos.length || undefined },
    { key: 'historial', label: SECTION_COPY.historial.label, icon: SECTION_COPY.historial.icon },
  ];

  const parametrosFiltrados = useMemo(() => {
    const term = parametrosBusqueda.trim().toLowerCase();
    if (!term) return parametros;

    return parametros.filter(parametro => {
      const text = [
        parametro.slug,
        parametro.nombre,
        parametro.descripcion,
        parametro.valor,
        parametro.tipo,
        parametro.es_publico ? 'publico' : 'privado',
        parametro.estado ? 'activo' : 'inactivo',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return text.includes(term);
    });
  }, [parametros, parametrosBusqueda]);

  const vendedoresConPendientes = useMemo(() => {
    const unique = new Map<number, NonNullable<Viaje['vendedor']>>();
    pendingViajes.forEach(viaje => {
      if (viaje.vendedor) {
        unique.set(viaje.vendedor.id, viaje.vendedor);
      }
    });
    return [...unique.values()].sort((left, right) => left.nombre.localeCompare(right.nombre));
  }, [pendingViajes]);

  /** Conductores con entregas pendientes asignadas (principal o secundario) y cuántas tienen. */
  const conductoresConPendientes = useMemo(() => {
    const conteo = new Map<string, number>();
    pendingViajes.forEach(viaje => {
      [viaje.conductorId, viaje.secondaryConductorId].forEach(conductorId => {
        if (conductorId) conteo.set(conductorId, (conteo.get(conductorId) ?? 0) + 1);
      });
    });
    return conductores
      .filter(conductor => conteo.has(conductor.id))
      .map(conductor => ({ id: conductor.id, nombre: conductor.nombre, cantidad: conteo.get(conductor.id) ?? 0 }))
      .sort((left, right) => left.nombre.localeCompare(right.nombre));
  }, [conductores, pendingViajes]);

  const vendedoresConHistorial = useMemo(() => {
    return usuarios
      .filter(candidate => candidate.id && normalizeRole(candidate) === 'VENDEDOR')
      .map(candidate => ({
        id: candidate.id as number,
        nombre: `${(candidate.profile?.razon_social || candidate.username).trim()}${esUsuarioErp(candidate) ? ' · ERP' : ''}`,
      }))
      .sort((left, right) => left.nombre.localeCompare(right.nombre));
  }, [usuarios]);

  const conductoresConHistorial = useMemo(() => {
    return [...conductores].sort((left, right) => left.nombre.localeCompare(right.nombre));
  }, [conductores]);

  const openTripCountsByConductor = useMemo(() => {
    const counts = new Map<string, number>();

    [...pendingViajes, ...viajes].forEach(viaje => {
      if (!viaje.conductorId || viaje.estado === 'finalizado') return;
      counts.set(viaje.conductorId, (counts.get(viaje.conductorId) ?? 0) + 1);
    });

    return counts;
  }, [pendingViajes, viajes]);

  const historialTotalPaginas = useMemo(
    () => Math.max(1, Math.ceil(historialTotalRegistros / HISTORIAL_PAGE_SIZE)),
    [historialTotalRegistros],
  );

  const historialPaginaActual = Math.min(historialPagina, historialTotalPaginas);

  useEffect(() => {
    setHistorialPagina(current => Math.min(current, historialTotalPaginas));
  }, [historialTotalPaginas]);

  const viajesPendientesFiltrados = useMemo(() => {
    const numeroFacturaTerm = numeroFacturaFiltro.trim().toLowerCase();

    return [...pendingViajes]
      .filter(viaje => vendedorFiltro === 'todos' || String(viaje.vendedor?.id ?? '') === vendedorFiltro)
      .filter(viaje => tipoEntregaAsignacionFiltro === 'todas' || viaje.tipoEntrega === tipoEntregaAsignacionFiltro)
      .filter(viaje => {
        if (conductorAsignacionFiltro === 'todos') return true;
        if (conductorAsignacionFiltro === 'sin_asignar') return !viaje.conductorId && !viaje.secondaryConductorId;
        return viaje.conductorId === conductorAsignacionFiltro || viaje.secondaryConductorId === conductorAsignacionFiltro;
      })
      .filter(viaje => {
        if (!numeroFacturaTerm) return true;

        return getViajeInvoiceNumbers(viaje)
          .some(invoiceNumber => invoiceNumber.toLowerCase().includes(numeroFacturaTerm));
      })
      .sort((left, right) => {
        const leftDate = left.fechaInicio ?? 0;
        const rightDate = right.fechaInicio ?? 0;
        return rightDate - leftDate;
      });
  }, [conductorAsignacionFiltro, numeroFacturaFiltro, pendingViajes, vendedorFiltro, tipoEntregaAsignacionFiltro]);

  function getConductoresAsignablesParaViaje(viaje: Viaje): Conductor[] {
    const candidates = [...conductoresActivos];
    const currentConductor = conductores.find(conductor => conductor.id === viaje.conductorId);

    if (currentConductor && !candidates.some(conductor => conductor.id === currentConductor.id)) {
      candidates.push(currentConductor);
    }

    return candidates.sort((left, right) => left.nombre.localeCompare(right.nombre));
  }

  function getConductoresAsignablesParaVehiculo(vehiculo: Vehiculo): Conductor[] {
    return conductoresActivos;
  }

  function getVehiculosAsignadosConductor(conductorId: string): Vehiculo[] {
    return vehiculos
      .filter(vehiculo => vehiculo.conductor?.id === conductorId)
      .sort((left, right) => left.placa.localeCompare(right.placa));
  }

  function handleAbrirAsignacionViaje(viaje: Viaje) {
    setSeleccionConductorPorViaje(prev => ({
      ...prev,
      [viaje.id]: prev[viaje.id] ?? viaje.conductorId ?? '',
    }));
    setSeleccionVehiculoPorViaje(prev => ({
      ...prev,
      [viaje.id]: prev[viaje.id] ?? viaje.vehiculoId ?? '',
    }));
    if (viaje.tipoEntrega === 'transferencia') {
      setSeleccionConductorSecundarioPorViaje(prev => ({
        ...prev,
        [viaje.id]: prev[viaje.id] ?? viaje.secondaryConductorId ?? '',
      }));
      setSeleccionVehiculoSecundarioPorViaje(prev => ({
        ...prev,
        [viaje.id]: prev[viaje.id] ?? viaje.secondaryVehiculoId ?? '',
      }));
    }
    setViajeEnAsignacion(viaje);
    setDataError('');
  }

  const asignacionViajeActual = viajeEnAsignacion ? (() => {
    const viaje = viajeEnAsignacion;
    const esTransferencia = viaje.tipoEntrega === 'transferencia';
    const conductorActual = conductores.find(conductor => conductor.id === viaje.conductorId);
    const conductorSecundarioActual = conductores.find(conductor => conductor.id === viaje.secondaryConductorId);
    const conductoresAsignables = getConductoresAsignablesParaViaje(viaje);
    const conductoresSecundariosAsignables = esTransferencia ? [...conductoresAsignables] : [];
    if (esTransferencia && conductorSecundarioActual && !conductoresSecundariosAsignables.some(conductor => conductor.id === conductorSecundarioActual.id)) {
      conductoresSecundariosAsignables.push(conductorSecundarioActual);
      conductoresSecundariosAsignables.sort((left, right) => left.nombre.localeCompare(right.nombre));
    }

    const conductorSeleccionadoId = seleccionConductorPorViaje[viaje.id] ?? viaje.conductorId ?? '';
    const vehiculoSeleccionadoId = seleccionVehiculoPorViaje[viaje.id] ?? viaje.vehiculoId ?? '';
    const vehiculosAsignados = conductorSeleccionadoId ? getVehiculosAsignadosConductor(conductorSeleccionadoId) : [];
    const conductorSecundarioSeleccionadoId = seleccionConductorSecundarioPorViaje[viaje.id] ?? viaje.secondaryConductorId ?? '';
    const vehiculoSecundarioSeleccionadoId = seleccionVehiculoSecundarioPorViaje[viaje.id] ?? viaje.secondaryVehiculoId ?? '';
    const vehiculosSecundariosAsignados = esTransferencia && conductorSecundarioSeleccionadoId
      ? getVehiculosAsignadosConductor(conductorSecundarioSeleccionadoId)
      : [];
    const vehiculoActual = vehiculos.find(vehiculo => vehiculo.id === viaje.vehiculoId) ?? viaje.vehiculo ?? null;
    const vehiculoSecundarioActual = esTransferencia
      ? (vehiculos.find(vehiculo => vehiculo.id === viaje.secondaryVehiculoId) ?? null)
      : null;

    return {
      viaje,
      esTransferencia,
      conductorActual,
      conductorSecundarioActual,
      conductoresAsignables,
      conductoresSecundariosAsignables,
      conductorSeleccionadoId,
      vehiculoSeleccionadoId,
      vehiculosAsignados,
      conductorSecundarioSeleccionadoId,
      vehiculoSecundarioSeleccionadoId,
      vehiculosSecundariosAsignados,
      vehiculoActual,
      vehiculoSecundarioActual,
    };
  })() : null;

  async function handleAsignarViaje(viaje: Viaje) {
    const esTransferencia = viaje.tipoEntrega === 'transferencia';
    const conductorId = seleccionConductorPorViaje[viaje.id] ?? viaje.conductorId ?? '';
    const vehiculoId = seleccionVehiculoPorViaje[viaje.id] ?? viaje.vehiculoId ?? '';
    const conductorSecundarioId = esTransferencia
      ? (seleccionConductorSecundarioPorViaje[viaje.id] ?? viaje.secondaryConductorId ?? '')
      : '';
    const vehiculoSecundarioId = esTransferencia
      ? (seleccionVehiculoSecundarioPorViaje[viaje.id] ?? viaje.secondaryVehiculoId ?? '')
      : '';
    if (!conductorId) {
      setDataError('Selecciona un conductor antes de asignar la entrega.');
      return;
    }
    if (!vehiculoId) {
      setDataError('Selecciona un vehículo asignado al conductor antes de asignar la entrega.');
      return;
    }

    const conductor = conductores.find(candidate => candidate.id === conductorId);
    const vehiculoSeleccionado = vehiculos.find(candidate => candidate.id === vehiculoId);
    const conductorSecundario = conductores.find(candidate => candidate.id === conductorSecundarioId);
    const vehiculoSecundarioSeleccionado = vehiculos.find(candidate => candidate.id === vehiculoSecundarioId);
    const picoYPlacaWarnings: string[] = [];
    if (!vehiculoSeleccionado || vehiculoSeleccionado.conductor?.id !== conductorId) {
      setDataError('El vehículo seleccionado no está asignado al conductor.');
      return;
    }
    if (vehiculoSeleccionado.etiqueta) {
      await alertar({ titulo: 'Vehículo no disponible', mensaje: 'No se puede asignar esta entrega con un vehículo en mantenimiento, avería o taller.' });
      return;
    }
    if (vehiculoSeleccionado.picoYPlacaDetalle?.activo) {
      picoYPlacaWarnings.push(formatPicoYPlacaMessage(
        vehiculoSeleccionado.picoYPlacaDetalle,
        `Advertencia: ${conductor?.nombre || 'este conductor'} tiene un vehículo con restricción de pico y placa. La entrega se asignará de todas formas.`,
      ));
    }

    if (esTransferencia) {
      if (!conductorSecundarioId) {
        setDataError('Selecciona el segundo conductor para esta transferencia.');
        return;
      }
      if (!vehiculoSecundarioId) {
        setDataError('Selecciona el vehículo del segundo conductor para esta transferencia.');
        return;
      }
      if (conductorSecundarioId === conductorId) {
        setDataError('El segundo conductor debe ser diferente al conductor principal.');
        return;
      }
      if (!vehiculoSecundarioSeleccionado || vehiculoSecundarioSeleccionado.conductor?.id !== conductorSecundarioId) {
        setDataError('El vehículo secundario seleccionado no está asignado al segundo conductor.');
        return;
      }
      if (vehiculoSecundarioSeleccionado.id === vehiculoSeleccionado.id) {
        setDataError('El segundo conductor debe tener un vehículo distinto al principal.');
        return;
      }
      if (vehiculoSecundarioSeleccionado.etiqueta) {
        await alertar({ titulo: 'Vehículo no disponible', mensaje: 'No se puede asignar esta transferencia con un segundo vehículo en mantenimiento, avería o taller.' });
        return;
      }
      if (vehiculoSecundarioSeleccionado.picoYPlacaDetalle?.activo) {
        picoYPlacaWarnings.push(formatPicoYPlacaMessage(
          vehiculoSecundarioSeleccionado.picoYPlacaDetalle,
          `Advertencia: ${conductorSecundario?.nombre || 'este conductor'} tiene un segundo vehículo con restricción de pico y placa. La entrega se asignará de todas formas.`,
        ));
      }
    }

    if (picoYPlacaWarnings.length > 0) {
      await alertar({ titulo: 'Aviso de pico y placa', mensaje: picoYPlacaWarnings.join('\n\n') });
    }

    try {
      setAsignandoViajeId(viaje.id);
      const viajeAsignado = await assignViaje(
        viaje.id,
        conductorId,
        vehiculoId,
        conductorSecundarioId || undefined,
        vehiculoSecundarioId || undefined,
      );

      if (esTransferencia && (
        viajeAsignado.secondaryConductorId !== conductorSecundarioId
        || viajeAsignado.secondaryVehiculoId !== vehiculoSecundarioId
      )) {
        throw new Error('La asignación secundaria no se guardó correctamente. Intenta nuevamente.');
      }

      setSeleccionConductorPorViaje(prev => ({ ...prev, [viaje.id]: viajeAsignado.conductorId ?? conductorId }));
      setSeleccionVehiculoPorViaje(prev => ({ ...prev, [viaje.id]: viajeAsignado.vehiculoId ?? vehiculoId }));
      if (esTransferencia) {
        setSeleccionConductorSecundarioPorViaje(prev => ({
          ...prev,
          [viaje.id]: viajeAsignado.secondaryConductorId ?? conductorSecundarioId,
        }));
        setSeleccionVehiculoSecundarioPorViaje(prev => ({
          ...prev,
          [viaje.id]: viajeAsignado.secondaryVehiculoId ?? vehiculoSecundarioId,
        }));
      }

      await refresh();
      setResumenAsignacion({
        viajeNombre: viaje.paquete?.nombre || 'Entrega asignada',
        asignaciones: [
          {
            titulo: 'Conductor principal',
            conductorNombre: conductor?.nombre || 'Conductor seleccionado',
            vehiculoNombre: formatVehiculoResumen(vehiculoSeleccionado),
          },
          ...(esTransferencia
            ? [{
                titulo: 'Conductor secundario',
                conductorNombre: conductorSecundario?.nombre || 'Conductor secundario',
                vehiculoNombre: formatVehiculoResumen(vehiculoSecundarioSeleccionado),
              }]
            : []),
        ],
      });
      setViajeEnAsignacion(current => (current?.id === viaje.id ? null : current));
      setDataError('');
    } catch (error: unknown) {
      const result = extractPicoYPlacaError(error);
      if (result.restriction) {
        await alertar({ titulo: 'Restricción de pico y placa', mensaje: formatPicoYPlacaMessage(result.restriction, result.message), tono: 'error' });
        return;
      }
      avisar(result.message, 'error');
      setDataError(result.message);
    } finally {
      setAsignandoViajeId(current => (current === viaje.id ? null : current));
    }
  }

  async function handleVerDetalleViaje(viaje: Viaje) {
    setDetalleViaje(viaje);

    try {
      const viajeActualizado = await getViajeById(viaje.id);
      if (viajeActualizado) {
        setDetalleViaje(current => (current?.id === viaje.id ? viajeActualizado : current));
      }
    } catch {
      return;
    }
  }

  async function handleEditarViaje(viaje: Viaje) {
    try {
      setCargandoEdicionViajeId(viaje.id);
      const viajeActual = await getViajeById(viaje.id);
      const viajeEditable = viajeActual ?? viaje;

      if (viajeEditable.conductorId || viajeEditable.secondaryConductorId) {
        setDataError('La entrega ya tiene conductor asignado y ya no se puede editar desde esta tabla.');
        await refresh();
        return;
      }

      setEditingViaje(viajeEditable);
      setDataError('');
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'No se pudo abrir la edición de la entrega.');
    } finally {
      setCargandoEdicionViajeId(null);
    }
  }

  async function handleAsignarVehiculo(vehiculo: Vehiculo) {
    const conductorId = seleccionConductorPorVehiculo[vehiculo.id] ?? vehiculo.conductor?.id ?? '';
    if (!conductorId) {
      setDataError('Selecciona un conductor antes de asignar el auto.');
      return;
    }
    if (vehiculo.etiqueta) {
      await alertar({ titulo: 'Vehículo no disponible', mensaje: 'No se puede asignar este auto mientras tenga una etiqueta de mantenimiento, avería o taller.' });
      return;
    }
    try {
      setAsignandoVehiculoId(vehiculo.id);
      await updateVehiculo(vehiculo.id, {
        placa: vehiculo.placa,
        marca: vehiculo.marca,
        modelo: vehiculo.modelo,
        anio: vehiculo.anio,
        tipoGasolina: vehiculo.tipoGasolina || 'extra',
        kilometrosGalon: Number(vehiculo.kilometrosGalon ?? 0),
        precioGalon: Number(vehiculo.precioGalon ?? 0),
        etiqueta: vehiculo.etiqueta,
        conductorId,
      });
      await refresh();
      setSeleccionConductorPorVehiculo(prev => {
        const { [vehiculo.id]: _descartado, ...resto } = prev;
        return resto;
      });
      avisar(`Vehículo ${vehiculo.placa} asignado a ${conductores.find(conductor => conductor.id === conductorId)?.nombre ?? 'el conductor'}.`);
      setDataError('');
    } catch (error: unknown) {
      const result = extractPicoYPlacaError(error);
      if (result.restriction) {
        await alertar({ titulo: 'Restricción de pico y placa', mensaje: formatPicoYPlacaMessage(result.restriction, result.message), tono: 'error' });
        return;
      }
      avisar(result.message, 'error');
    } finally {
      setAsignandoVehiculoId(current => (current === vehiculo.id ? null : current));
    }
  }

  async function handleQuitarAsignacionVehiculo(vehiculo: Vehiculo) {
    try {
      setAsignandoVehiculoId(vehiculo.id);
      await updateVehiculo(vehiculo.id, {
        placa: vehiculo.placa,
        marca: vehiculo.marca,
        modelo: vehiculo.modelo,
        anio: vehiculo.anio,
        tipoGasolina: vehiculo.tipoGasolina || 'extra',
        kilometrosGalon: Number(vehiculo.kilometrosGalon ?? 0),
        precioGalon: Number(vehiculo.precioGalon ?? 0),
        etiqueta: vehiculo.etiqueta,
        conductorId: null,
      });
      await refresh();
      setSeleccionConductorPorVehiculo(prev => {
        const { [vehiculo.id]: _descartado, ...resto } = prev;
        return resto;
      });
      avisar(`Se quitó el conductor del vehículo ${vehiculo.placa}.`);
      setDataError('');
    } catch (error: unknown) {
      avisar(extractPicoYPlacaError(error).message, 'error');
    } finally {
      setAsignandoVehiculoId(current => (current === vehiculo.id ? null : current));
    }
  }

  async function toggleUserStatus(target: UserWithProfile) {
    if (!target.id) return;
    const nextState = !(target.profile?.estado ?? true);

    try {
      await activarUsuario(target.id, { estado: nextState });
      await refresh();
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'No se pudo actualizar el estado del usuario.');
    }
  }

  async function handleDeleteUser(target: UserWithProfile) {
    if (!target.id) return;
    if (!(await confirmar({ titulo: `¿Eliminar al usuario ${target.username}?`, mensaje: 'Esta acción no se puede deshacer.', textoAceptar: 'Eliminar' }))) return;

    try {
      await eliminarUsuario(target.id);
      await refresh();
      avisar(`Usuario ${target.username} eliminado.`);
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'No se pudo eliminar el usuario.');
    }
  }

  async function handleResetPassword(target: UserWithProfile) {
    if (!target.id) return;
    const newPassword = await pedirTexto({
      titulo: 'Cambiar contraseña',
      mensaje: `Nueva contraseña para el usuario ${target.username}.`,
      etiqueta: 'Nueva contraseña',
      placeholder: 'Escribe la nueva contraseña',
      textoAceptar: 'Cambiar contraseña',
    });
    if (!newPassword) return;

    try {
      await resetearContrasena(target.id, { password: newPassword });
      setDataError('');
      avisar(`Contraseña actualizada para ${target.username}.`);
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'No se pudo cambiar la contraseña.');
    }
  }

  async function handleGuardarParametro() {
    if (!editingParametro?.id_parametro) return;

    try {
      setSavingParametro(true);
      await actualizarParametro(editingParametro.id_parametro, {
        valor: parametroValor,
      });
      await refreshParametros();
      setEditingParametro(null);
      setParametroValor('');
      setDataError('');
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'No se pudo actualizar el valor del parámetro.');
    } finally {
      setSavingParametro(false);
    }
  }

  async function handleArchivoParametro(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file || !editingParametro?.id_parametro) return;

    setUploadingParametroArchivo(true);
    try {
      const prefix = editingParametro.slug === 'sistema_logo_url' || editingParametro.slug === 'logo_factura_url'
        ? 'parametros/logos'
        : 'parametros';
      const nextUrl = await uploadFileToMinio(file, prefix);
      await actualizarParametro(editingParametro.id_parametro, {
        valor: nextUrl,
      });

      await refreshParametros();
      setParametroValor(nextUrl);
      setEditingParametro(current => (current ? { ...current, valor: nextUrl } : current));
      setDataError('');
      event.target.value = '';
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'No se pudo subir el archivo del parámetro.');
    } finally {
      setUploadingParametroArchivo(false);
    }
  }

  function resetSucursalForm() {
    setSucursalForm({
      nombre: '',
      direccion: '',
      telefono: '',
    });
  }

  async function handleGuardarSucursal() {
    if (!sucursalForm.nombre.trim() || !sucursalForm.direccion.trim() || !sucursalForm.telefono.trim()) {
      avisar('Completa nombre, dirección y teléfono de la sucursal.', 'advertencia');
      return;
    }

    try {
      setSavingSucursal(true);
      if (editingSucursal) {
        await updateSucursal(editingSucursal.id, {
          nombre: sucursalForm.nombre.trim(),
          direccion: sucursalForm.direccion.trim(),
          telefono: sucursalForm.telefono.trim(),
        });
      } else {
        await createSucursal({
          nombre: sucursalForm.nombre.trim(),
          direccion: sucursalForm.direccion.trim(),
          telefono: sucursalForm.telefono.trim(),
        });
      }
      await refreshSucursales();
      avisar(editingSucursal ? `Sucursal ${sucursalForm.nombre.trim()} actualizada.` : `Sucursal ${sucursalForm.nombre.trim()} creada.`);
      setEditingSucursal(null);
      setShowSucursalForm(false);
      resetSucursalForm();
      setDataError('');
    } catch (err: unknown) {
      avisar(mensajeDeError(err, 'No se pudo guardar la sucursal.'), 'error');
    } finally {
      setSavingSucursal(false);
    }
  }

  async function handleEliminarSucursal(target: Sucursal) {
    if (!(await confirmar({ titulo: `¿Eliminar la sucursal ${target.nombre}?`, mensaje: 'Esta acción no se puede deshacer.', textoAceptar: 'Eliminar' }))) return;

    try {
      await deleteSucursal(target.id);
      await refreshSucursales();
      avisar(`Sucursal ${target.nombre} eliminada.`);
      setDataError('');
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'No se pudo eliminar la sucursal.');
    }
  }

  return (
    <>
      <PanelShell
        roleLabel="Administrador"
        pageTitle="Panel administrativo"
        pageSubtitle="Operación, flota y entregas."
        currentLabel={SECTION_COPY[section].label}
        currentDetail={SECTION_COPY[section].detail}
        userName={user?.profile?.razon_social ?? user?.username ?? 'Administrador'}
        userMeta={user?.profile?.rol ?? 'ADMINISTRADOR'}
        navItems={navItems}
        activeKey={section}
        onSelect={key => setSection(key as Section)}
        notifications={dashboardStats.notifications}
        onLogout={logout}
        viewModeToggle
        aparienciaPersonalizable
      >
        {dataError && (
          <div className="mb-6 rounded-[var(--radius-panel)] border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700 shadow-sm">
            {dataError}
          </div>
        )}

        {section === 'dashboard' && (
          <section className="space-y-6">
            <div className="grid gap-4 xl:grid-cols-4">
              {dashboardStats.summary.map(stat => (
                <DashboardStatCard
                  key={stat.label}
                  icon={stat.icon}
                  label={stat.label}
                  value={stat.value}
                  trend={stat.trend}
                  trendTone={stat.trendTone}
                />
              ))}
            </div>

            <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
              <WeeklyBarChart
                title="Envíos semanal"
                subtitle="Conteo de rutas iniciadas o cerradas durante la semana actual."
                data={dashboardStats.weeklySeries}
              />
              <ActivityFeed
                title="Actividad reciente"
                subtitle="Movimientos inmediatos de viajes en curso y cierres registrados."
                items={dashboardStats.recentActivity}
              />
            </div>
          </section>
        )}

          {section === 'personal' && (
            <section className="space-y-5">
              <div className="flex flex-col gap-3 2xl:flex-row 2xl:items-center 2xl:justify-between">
                <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filtrar por rol">
                  {PERSONAL_ROLES.map(opcion => {
                    const activo = personalRol === opcion.valor;
                    const total = opcion.valor === 'todos' ? usuarios.length : (conteoPorRol.get(opcion.valor) ?? 0);
                    return (
                      <button
                        key={opcion.valor}
                        type="button"
                        role="tab"
                        aria-selected={activo}
                        onClick={() => setPersonalRol(opcion.valor)}
                        className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-semibold transition duration-200 ${
                          activo
                            ? 'border-[var(--freisa-black)] bg-[var(--freisa-black)] text-white'
                            : 'border-stone-200 bg-white text-stone-600 hover:border-stone-300 hover:text-stone-900'
                        }`}
                      >
                        {opcion.etiqueta}
                        <span className={`rounded-full px-2 py-0.5 text-xs ${activo ? 'bg-white/15' : 'bg-stone-100 text-stone-500'}`}>{total}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <div className="relative sm:flex-1 2xl:w-80 2xl:flex-none">
                    <i className="fa-solid fa-magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-stone-400" aria-hidden="true" />
                    <input
                      value={personalBusqueda}
                      onChange={event => setPersonalBusqueda(event.target.value)}
                      placeholder="Buscar por nombre, usuario, correo o cédula"
                      aria-label="Buscar personal"
                      className="ui-input pl-9"
                    />
                  </div>
                  <select
                    value={personalEstado}
                    onChange={event => setPersonalEstado(event.target.value as 'todos' | 'activos' | 'inactivos')}
                    aria-label="Filtrar por estado"
                    className="ui-input sm:w-40"
                  >
                    <option value="todos">Todos los estados</option>
                    <option value="activos">Activos</option>
                    <option value="inactivos">Inactivos</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingUser(null);
                      setShowUserForm(true);
                    }}
                    className="ui-btn ui-btn-primary whitespace-nowrap"
                  >
                    <i className="fa-solid fa-user-plus" aria-hidden="true" />
                    Nuevo usuario
                  </button>
                </div>
              </div>

              <div className="ui-card overflow-hidden">
                <div className="flex items-center justify-between border-b border-stone-200 px-5 py-3 text-sm text-stone-500">
                  <span>
                    {personalFiltrado.length === usuarios.length
                      ? `${usuarios.length} usuarios`
                      : `${personalFiltrado.length} de ${usuarios.length} usuarios`}
                  </span>
                  {(personalBusqueda || personalRol !== 'todos' || personalEstado !== 'todos') && (
                    <button
                      type="button"
                      onClick={() => {
                        setPersonalBusqueda('');
                        setPersonalRol('todos');
                        setPersonalEstado('todos');
                      }}
                      className="text-sm font-semibold text-[var(--freisa-red)] hover:underline"
                    >
                      Limpiar filtros
                    </button>
                  )}
                </div>
                {personalFiltrado.length === 0 ? (
                  <div className="p-10 text-center text-sm text-stone-500">
                    {usuarios.length === 0 ? 'No hay usuarios registrados.' : 'Ningún usuario coincide con los filtros.'}
                  </div>
                ) : (
                  <div className="overflow-x-auto lg:max-h-[70vh] lg:overflow-auto">
                    <table className="ui-tabla ui-tabla-fija tabla-apilada min-w-full text-left text-sm text-stone-600">
                      <thead className="bg-stone-50 text-xs uppercase tracking-[0.16em] text-stone-500">
                        <tr>
                          <th className="px-4 py-3 font-semibold">Nombre</th>
                          <th className="px-4 py-3 font-semibold">Usuario</th>
                          <th className="px-4 py-3 font-semibold">Rol</th>
                          <th className="px-4 py-3 font-semibold">Sucursal</th>
                          <th className="px-4 py-3 font-semibold">Identificación</th>
                          <th className="px-4 py-3 font-semibold">Estado</th>
                          <th className="px-4 py-3 text-right font-semibold">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {personalFiltrado.map(candidate => {
                          const role = normalizeRole(candidate);
                          const activo = candidate.profile?.estado !== false;
                          const hasConductorProfile = candidate.id ? userIdsWithConductorProfile.has(candidate.id) : false;
                          const sucursalNombre = sucursales.find(suc => Number(suc.id) === candidate.profile?.sucursal)?.nombre;
                          const deErp = esUsuarioErp(candidate);
                          const vinculadoA = deErp && candidate.profile?.vinculado_a
                            ? usuarios.find(otro => otro.id === candidate.profile?.vinculado_a)?.username
                            : undefined;
                          const avisoVinculo = deErp && !vinculadoA ? textoAvisoVinculoErp(candidate.profile?.vinculo_aviso) : '';
                          return (
                            <tr key={candidate.id ?? candidate.username} className="border-t border-stone-100 align-middle">
                              <td data-label="Nombre" className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-stone-100 text-xs font-bold uppercase text-stone-600">
                                    {(candidate.profile?.razon_social || candidate.username || '?').trim().charAt(0)}
                                  </span>
                                  <div className="min-w-0">
                                    <p className="font-semibold text-stone-900">{candidate.profile?.razon_social || candidate.username}</p>
                                    <p className="truncate text-xs text-stone-500">{candidate.email || 'Sin correo registrado'}</p>
                                    {deErp && (
                                      <p className="text-[11px] text-indigo-700">
                                        Del ERP{candidate.profile?.bodega_externa ? ` · bodega ${candidate.profile.bodega_externa}` : ''}
                                        {vinculadoA ? ` · viajes a ${vinculadoA}${candidate.profile?.vinculo_manual ? ' (manual)' : ''}` : ''}
                                      </p>
                                    )}
                                    {avisoVinculo && (
                                      <p className="text-[11px] font-medium text-amber-700">
                                        <i className="fa-solid fa-triangle-exclamation mr-1" aria-hidden="true" />
                                        Revisar datos: {avisoVinculo}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td data-label="Usuario" className="px-4 py-3 font-mono text-xs text-stone-700">{candidate.username}</td>
                              <td data-label="Rol" className="px-4 py-3">
                                <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${roleBadgeClasses(role)}`}>{role}</span>
                                {deErp && <span title="Vendedor sincronizado desde el ERP" className="ml-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 align-middle text-[10px] font-bold text-indigo-700">ERP</span>}
                                {avisoVinculo && <span title={avisoVinculo} className="ml-1.5 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 align-middle text-[10px] font-bold text-amber-700">Revisar</span>}
                                {role === 'CONDUCTOR' && !hasConductorProfile && (
                                  <p className="mt-1 text-[11px] font-medium text-amber-700">Perfil de conductor pendiente</p>
                                )}
                              </td>
                              <td data-label="Sucursal" className="px-4 py-3">{sucursalNombre || '—'}</td>
                              <td data-label="Identificación" className="px-4 py-3">{candidate.profile?.identificacion || '—'}</td>
                              <td data-label="Estado" className="px-4 py-3">
                                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${activo ? 'text-emerald-700' : 'text-stone-400'}`}>
                                  <span className={`h-2 w-2 rounded-full ${activo ? 'bg-emerald-500' : 'bg-stone-300'}`} />
                                  {activo ? 'Activo' : 'Inactivo'}
                                </span>
                              </td>
                              <td data-label="" className="whitespace-nowrap px-4 py-3">
                                {deErp ? (
                                  <div className="flex items-center justify-end gap-2">
                                    <span className="text-xs font-medium text-stone-500" title="Sus datos y su estado se administran en el ERP">
                                      <i className="fa-solid fa-lock mr-1" aria-hidden="true" />
                                      Se administra en el ERP
                                    </span>
                                    <button
                                      type="button"
                                      title="Vincular con un vendedor del GPS"
                                      aria-label={`Vincular a ${candidate.username} con un vendedor del GPS`}
                                      onClick={() => setVinculandoErp(candidate)}
                                      className="ui-btn ui-btn-info ui-btn-sm"
                                    >
                                      <i className="fa-solid fa-link" aria-hidden="true" />
                                    </button>
                                  </div>
                                ) : (
                                <div className="flex justify-end gap-1.5">
                                  <button
                                    type="button"
                                    title="Editar usuario"
                                    aria-label={`Editar a ${candidate.username}`}
                                    onClick={() => {
                                      setEditingUser(candidate);
                                      setShowUserForm(false);
                                    }}
                                    className="ui-btn ui-btn-secondary ui-btn-sm"
                                  >
                                    <i className="fa-solid fa-pen" aria-hidden="true" />
                                  </button>
                                  <button
                                    type="button"
                                    title="Cambiar contraseña"
                                    aria-label={`Cambiar contraseña de ${candidate.username}`}
                                    onClick={() => void handleResetPassword(candidate)}
                                    className="ui-btn ui-btn-info ui-btn-sm"
                                  >
                                    <i className="fa-solid fa-key" aria-hidden="true" />
                                  </button>
                                  <button
                                    type="button"
                                    title={activo ? 'Desactivar usuario' : 'Activar usuario'}
                                    aria-label={`${activo ? 'Desactivar' : 'Activar'} a ${candidate.username}`}
                                    onClick={() => void toggleUserStatus(candidate)}
                                    className="ui-btn ui-btn-warning ui-btn-sm"
                                  >
                                    <i className={`fa-solid ${activo ? 'fa-user-slash' : 'fa-user-check'}`} aria-hidden="true" />
                                  </button>
                                  <button
                                    type="button"
                                    title="Eliminar usuario"
                                    aria-label={`Eliminar a ${candidate.username}`}
                                    onClick={() => void handleDeleteUser(candidate)}
                                    className="ui-btn ui-btn-danger ui-btn-sm"
                                  >
                                    <i className="fa-solid fa-trash-can" aria-hidden="true" />
                                  </button>
                                </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </section>
          )}

          {section === 'asignaciones' && (
            <section className="space-y-6">
              <div>
                <div className="ui-filtros ui-filtros-5 w-full">
                  <div className="ui-filtro-busqueda">
                    <label className="ui-filtro-label">Buscar por número de factura</label>
                    <input
                      value={numeroFacturaFiltro}
                      onChange={event => setNumeroFacturaFiltro(event.target.value)}
                      placeholder="Coincidencia parcial del número"
                      className="ui-input"
                    />
                  </div>
                  <div>
                    <label className="ui-filtro-label">Filtrar por vendedor</label>
                    <select
                      value={vendedorFiltro}
                      onChange={event => setVendedorFiltro(event.target.value)}
                      className="ui-input"
                    >
                      <option value="todos">Todos los vendedores</option>
                      {vendedoresConPendientes.map(vendedor => (
                        <option key={vendedor.id} value={String(vendedor.id)}>
                          {vendedor.nombre}{vendedor.esErp ? ' · ERP' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="ui-filtro-label">Filtrar por conductor</label>
                    <select
                      value={conductorAsignacionFiltro}
                      onChange={event => setConductorAsignacionFiltro(event.target.value)}
                      className="ui-input"
                    >
                      <option value="todos">Todos los conductores</option>
                      <option value="sin_asignar">Sin conductor asignado</option>
                      {conductoresConPendientes.map(conductor => (
                        <option key={conductor.id} value={conductor.id}>
                          {conductor.nombre} ({conductor.cantidad})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="ui-filtro-label">Tipo de entrega</label>
                    <select
                      value={tipoEntregaAsignacionFiltro}
                      onChange={event => setTipoEntregaAsignacionFiltro(event.target.value)}
                      className="ui-input"
                    >
                      <option value="todas">Todas</option>
                      <option value="entrega">Entrega</option>
                      <option value="envio">Envío</option>
                      <option value="transferencia">Transferencia</option>
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() => void refresh()}
                    className="ui-btn ui-btn-info"
                  >
                    Actualizar
                  </button>
                </div>
              </div>

              <div className="grid gap-4 xl:grid-cols-3">
                <article className="ui-card p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Pendientes</p>
                  <p className="mt-4 text-3xl font-semibold text-stone-950">{pendingViajes.length}</p>
                </article>
                <article className="ui-card p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Sin conductor</p>
                  <p className="mt-4 text-3xl font-semibold text-stone-950">{pendingViajes.filter(viaje => !viaje.conductorId).length}</p>
                </article>
                <article className="ui-card p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Vendedores con pendientes</p>
                  <p className="mt-4 text-3xl font-semibold text-stone-950">{vendedoresConPendientes.length}</p>
                </article>
              </div>

              {viajesPendientesFiltrados.length === 0 ? (
                <div className="rounded-[var(--radius-card)] border border-dashed border-stone-300 bg-white/70 p-12 text-center text-stone-500">
                  No hay entregas pendientes para el filtro seleccionado.
                </div>
              ) : (
                <div className="ui-card overflow-hidden">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-200 px-5 py-4">
                    <div>
                      <h4 className="text-lg font-semibold text-stone-900">Entregas pendientes por asignar</h4>
                      <p className="text-sm text-stone-500">Tabla operativa para asignar o encolar varias entregas por conductor.</p>
                    </div>
                    <p className="text-sm text-stone-500">{viajesPendientesFiltrados.length} registros</p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="ui-tabla tabla-apilada min-w-full text-left text-sm text-stone-600">
                      <thead className="bg-stone-50 text-xs uppercase tracking-[0.16em] text-stone-500">
                        <tr>
                          <th className="px-5 py-3 font-semibold">Fecha de creación</th>
                          <th className="px-5 py-3 font-semibold">Vendedor</th>
                          <th className="px-5 py-3 font-semibold">Entrega</th>
                          <th className="px-5 py-3 font-semibold">Url Ubicacion Destino</th>
                          <th className="px-5 py-3 font-semibold">Tipo</th>
                          <th className="px-5 py-3 font-semibold">Número de factura</th>
                          <th className="px-5 py-3 font-semibold">Conductor actual</th>
                          <th className="px-5 py-3 font-semibold text-right">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viajesPendientesFiltrados.map(viaje => {
                          const esTransferencia = viaje.tipoEntrega === 'transferencia';
                          const conductorActual = conductores.find(conductor => conductor.id === viaje.conductorId);
                          const conductorSecundarioActual = conductores.find(conductor => conductor.id === viaje.secondaryConductorId);
                          const vehiculoSecundarioActual = vehiculos.find(vehiculo => vehiculo.id === viaje.secondaryVehiculoId);
                          const viajesAbiertosActuales = conductorActual ? openTripCountsByConductor.get(conductorActual.id) ?? 0 : 0;
                          const ubicacionFinal = getViajeFinalLocationLabel(viaje);
                          const mapaDestino = getViajeDestinationUrl(viaje);
                          const puedeEditarAntesDeAsignar = !viaje.conductorId && !viaje.secondaryConductorId;
                          const invoiceNumbers = getViajeInvoiceNumbers(viaje);

                          return (
                            <tr key={viaje.id} className="border-t border-stone-100 align-top">
                              <td data-label="Fecha de creación" className="px-5 py-4 whitespace-nowrap text-stone-700">{formatShortDateTime(viaje.createdAt)}</td>
                              <td data-label="Vendedor" className="px-5 py-4 font-medium text-stone-900">{viaje.vendedor?.nombre || viaje.vendedor?.username || 'Sin vendedor'}{viaje.vendedor?.esErp && <span title="Vendedor sincronizado desde el ERP" className="ml-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 align-middle text-[10px] font-bold text-indigo-700">ERP</span>}</td>
                              <td data-label="Entrega" className="px-5 py-4 font-semibold text-stone-900">{viaje.paquete?.nombre || 'Paquete sin nombre'}</td>
                              <td data-label="Ubicación destino" className="px-5 py-4">
                                {mapaDestino ? (
                                  <a
                                    href={mapaDestino}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="font-medium text-blue-700 underline decoration-blue-200 underline-offset-4 transition hover:text-blue-900"
                                  >
                                    Abrir mapa
                                  </a>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td data-label="Tipo" className="px-5 py-4">
                                {tipoEntregaLabel(viaje.tipoEntrega)}
                                {viaje.tipoEntrega === 'envio' && (
                                  <p className="mt-1 text-xs font-semibold text-stone-400">
                                    {viaje.companiaTransporte || '—'}
                                  </p>
                                )}
                              </td>
                              <td data-label="Número de factura" className="px-5 py-4">
                                {invoiceNumbers.length > 0 ? (
                                  <div className="flex max-w-[260px] flex-wrap gap-2">
                                    {invoiceNumbers.map((invoiceNumber, index) => (
                                      <span
                                        key={`${viaje.id}-invoice-${index}`}
                                        className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-700"
                                      >
                                        {invoiceNumber}
                                      </span>
                                    ))}
                                  </div>
                                ) : '—'}
                              </td>
                              <td data-label="Conductor actual" className="px-5 py-4">
                                <div className="space-y-1">
                                  <p>
                                    {conductorActual ? `${conductorActual.nombre}${viaje.vehiculo?.placa ? ` · ${viaje.vehiculo.placa}` : ''} · ${viajesAbiertosActuales} viaje${viajesAbiertosActuales === 1 ? '' : 's'}` : 'Sin asignar'}
                                  </p>
                                  {esTransferencia && (
                                    <p className="text-xs text-stone-500">
                                      Secundario: {conductorSecundarioActual ? `${conductorSecundarioActual.nombre}${vehiculoSecundarioActual?.placa ? ` · ${vehiculoSecundarioActual.placa}` : ''}` : 'Sin asignar'}
                                    </p>
                                  )}
                                </div>
                              </td>
                              <td data-label="" className="px-5 py-4 text-right">
                                <div className="flex flex-wrap justify-end gap-2">
                                  {puedeEditarAntesDeAsignar && (
                                    <button
                                      onClick={() => void handleEditarViaje(viaje)}
                                      disabled={cargandoEdicionViajeId === viaje.id}
                                      className="ui-btn ui-btn-success ui-btn-sm"
                                    >
                                      {cargandoEdicionViajeId === viaje.id ? 'Abriendo...' : 'Editar entrega'}
                                    </button>
                                  )}
                                  <button
                                    onClick={() => void handleVerDetalleViaje(viaje)}
                                    className="ui-btn ui-btn-info ui-btn-sm"
                                  >
                                    Ver detalles
                                  </button>
                                  {esTransferencia ? (
                                    <button
                                      onClick={() => handleAbrirAsignacionViaje(viaje)}
                                      disabled={asignandoViajeId === viaje.id}
                                      className="ui-btn ui-btn-primary"
                                    >
                                      {asignandoViajeId === viaje.id ? 'Asignando...' : viaje.secondaryConductorId ? 'Reasignar transferencia' : 'Asignar transferencia'}
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => handleAbrirAsignacionViaje(viaje)}
                                      disabled={asignandoViajeId === viaje.id}
                                      className="ui-btn ui-btn-primary"
                                    >
                                      {asignandoViajeId === viaje.id ? 'Asignando...' : viaje.conductorId ? 'Reasignar' : 'Asignar'}
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </section>
          )}

          {section === 'vehiculos' && (
            <section className="space-y-5">
              <div className="flex flex-col gap-3 2xl:flex-row 2xl:items-center 2xl:justify-between">
                <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filtrar vehículos por estado">
                  {VEHICULO_FILTROS.map(opcion => {
                    const activo = vehiculoFiltroEstado === opcion.valor;
                    return (
                      <button
                        key={opcion.valor}
                        type="button"
                        role="tab"
                        aria-selected={activo}
                        onClick={() => setVehiculoFiltroEstado(opcion.valor)}
                        className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-semibold transition duration-200 ${
                          activo
                            ? 'border-[var(--freisa-black)] bg-[var(--freisa-black)] text-white'
                            : 'border-stone-200 bg-white text-stone-600 hover:border-stone-300 hover:text-stone-900'
                        }`}
                      >
                        <span className={`h-2 w-2 rounded-full ${opcion.punto}`} />
                        {opcion.etiqueta}
                        <span className={`rounded-full px-2 py-0.5 text-xs ${activo ? 'bg-white/15' : 'bg-stone-100 text-stone-500'}`}>
                          {conteoVehiculos[opcion.valor]}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <div className="relative sm:flex-1 2xl:w-80 2xl:flex-none">
                    <i className="fa-solid fa-magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-stone-400" aria-hidden="true" />
                    <input
                      value={vehiculoBusqueda}
                      onChange={event => setVehiculoBusqueda(event.target.value)}
                      placeholder="Buscar por placa, marca, modelo o conductor"
                      aria-label="Buscar vehículo"
                      className="ui-input pl-9"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingVehiculo(null);
                      setShowVehiculoForm(true);
                    }}
                    className="ui-btn ui-btn-primary"
                  >
                    <i className="fa-solid fa-plus" aria-hidden="true" />
                    Nuevo auto
                  </button>
                </div>
              </div>

              {conductores.length === 0 && (
                <div className="rounded-[var(--radius-panel)] border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800">
                  No hay conductores activos sincronizados. Asigna rol Conductor a un usuario activo en Gestión de Personal.
                </div>
              )}

              <div className="ui-card overflow-hidden">
                <div className="flex items-center justify-between border-b border-stone-200 px-5 py-3 text-sm text-stone-500">
                  <span>
                    {vehiculosFiltrados.length === vehiculos.length
                      ? `${vehiculos.length} vehículos`
                      : `${vehiculosFiltrados.length} de ${vehiculos.length} vehículos`}
                  </span>
                  {(vehiculoBusqueda || vehiculoFiltroEstado !== 'todos') && (
                    <button
                      type="button"
                      onClick={() => {
                        setVehiculoBusqueda('');
                        setVehiculoFiltroEstado('todos');
                      }}
                      className="text-sm font-semibold text-[var(--freisa-red)] hover:underline"
                    >
                      Limpiar filtros
                    </button>
                  )}
                </div>
                {vehiculosFiltrados.length === 0 ? (
                  <div className="p-10 text-center text-sm text-stone-500">
                    {vehiculos.length === 0 ? 'No hay vehículos registrados.' : 'Ningún vehículo coincide con los filtros.'}
                  </div>
                ) : (
                  <div className="overflow-x-auto lg:max-h-[72vh] lg:overflow-auto">
                    <table className="ui-tabla ui-tabla-fija tabla-apilada min-w-full text-left text-sm text-stone-600">
                      <thead className="bg-stone-50 text-xs uppercase tracking-[0.16em] text-stone-500">
                        <tr>
                          <th className="px-4 py-3 font-semibold">Vehículo</th>
                          <th className="px-4 py-3 font-semibold">Estado</th>
                          <th className="px-4 py-3 font-semibold">Combustible</th>
                          <th className="px-4 py-3 font-semibold">Conductor asignado</th>
                          <th className="px-4 py-3 text-right font-semibold">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vehiculosFiltrados.map(vehiculo => {
                          const estado = estadoVehiculo(vehiculo);
                          const seleccion = seleccionConductorPorVehiculo[vehiculo.id] ?? vehiculo.conductor?.id ?? '';
                          const cambioPendiente = Boolean(seleccion) && seleccion !== (vehiculo.conductor?.id ?? '');
                          const guardando = asignandoVehiculoId === vehiculo.id;
                          return (
                            <tr key={vehiculo.id} className="border-t border-stone-100 align-middle">
                              <td data-label="Vehículo" className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-stone-100 text-stone-600">
                                    <i className="fa-solid fa-car-side" aria-hidden="true" />
                                  </span>
                                  <div className="min-w-0">
                                    <p className="font-mono text-sm font-bold tracking-wider text-stone-900">{vehiculo.placa}</p>
                                    <p className="truncate text-xs text-stone-500">
                                      {[vehiculo.marca, vehiculo.modelo, vehiculo.anio].filter(Boolean).join(' · ') || '—'}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td data-label="Estado" className="px-4 py-3">
                                <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold ${estado.clases}`}>{estado.texto}</span>
                                <p className="mt-1 max-w-[260px] text-xs text-stone-500">
                                  {vehiculo.picoYPlacaDetalle?.activo ? vehiculo.picoYPlacaDetalle.mensaje : getVehiculoAvailabilityText(vehiculo)}
                                </p>
                              </td>
                              <td data-label="Combustible" className="px-4 py-3">{tipoGasolinaLabel(vehiculo.tipoGasolina)}</td>
                              <td data-label="Conductor asignado" className="px-4 py-3">
                                <div className="flex min-w-[260px] items-center gap-1.5">
                                  <select
                                    value={seleccion}
                                    onChange={event => setSeleccionConductorPorVehiculo(prev => ({ ...prev, [vehiculo.id]: event.target.value }))}
                                    aria-label={`Conductor para ${vehiculo.placa}`}
                                    disabled={guardando}
                                    className="ui-input !py-2"
                                  >
                                    <option value="">Sin conductor</option>
                                    {getConductoresAsignablesParaVehiculo(vehiculo).map(conductor => (
                                      <option key={conductor.id} value={conductor.id}>
                                        {conductor.nombre}
                                      </option>
                                    ))}
                                  </select>
                                  {cambioPendiente && (
                                    <button
                                      type="button"
                                      onClick={() => void handleAsignarVehiculo(vehiculo)}
                                      disabled={guardando}
                                      title={vehiculo.conductor ? 'Guardar reasignación' : 'Asignar conductor'}
                                      className="ui-btn ui-btn-primary ui-btn-sm anim-pop-in"
                                    >
                                      {guardando ? <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" /> : <i className="fa-solid fa-check" aria-hidden="true" />}
                                      {vehiculo.conductor ? 'Reasignar' : 'Asignar'}
                                    </button>
                                  )}
                                  {vehiculo.conductor && !cambioPendiente && (
                                    <button
                                      type="button"
                                      onClick={() => void handleQuitarAsignacionVehiculo(vehiculo)}
                                      disabled={guardando}
                                      title="Quitar asignación"
                                      aria-label={`Quitar conductor de ${vehiculo.placa}`}
                                      className="ui-btn ui-btn-warning ui-btn-sm"
                                    >
                                      <i className="fa-solid fa-link-slash" aria-hidden="true" />
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td data-label="" className="whitespace-nowrap px-4 py-3">
                                <div className="flex justify-end gap-1.5">
                                  <button
                                    type="button"
                                    title="Editar vehículo"
                                    aria-label={`Editar ${vehiculo.placa}`}
                                    onClick={() => {
                                      setEditingVehiculo(vehiculo);
                                      setShowVehiculoForm(false);
                                    }}
                                    className="ui-btn ui-btn-secondary ui-btn-sm"
                                  >
                                    <i className="fa-solid fa-pen" aria-hidden="true" />
                                  </button>
                                  <button
                                    type="button"
                                    title="Eliminar vehículo"
                                    aria-label={`Eliminar ${vehiculo.placa}`}
                                    onClick={async () => {
                                      if (!(await confirmar({ titulo: `¿Eliminar el vehículo ${vehiculo.placa}?`, mensaje: 'Esta acción no se puede deshacer.', textoAceptar: 'Eliminar' }))) return;
                                      try {
                                        await deleteVehiculo(vehiculo.id);
                                        await refresh();
                                        avisar(`Vehículo ${vehiculo.placa} eliminado.`);
                                      } catch (err: unknown) {
                                        avisar(mensajeDeError(err, 'No se pudo eliminar el vehículo.'), 'error');
                                      }
                                    }}
                                    className="ui-btn ui-btn-danger ui-btn-sm"
                                  >
                                    <i className="fa-solid fa-trash-can" aria-hidden="true" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </section>
          )}

          {section === 'sucursales' && (
            <section className="space-y-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="relative sm:w-96">
                  <i className="fa-solid fa-magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-stone-400" aria-hidden="true" />
                  <input
                    value={sucursalBusqueda}
                    onChange={event => setSucursalBusqueda(event.target.value)}
                    placeholder="Buscar por nombre, dirección o teléfono"
                    aria-label="Buscar sucursal"
                    className="ui-input pl-9"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setEditingSucursal(null);
                    resetSucursalForm();
                    setShowSucursalForm(true);
                  }}
                  className="ui-btn ui-btn-primary"
                >
                  <i className="fa-solid fa-plus" aria-hidden="true" />
                  Nueva sucursal
                </button>
              </div>

              <div className="ui-card overflow-hidden">
                <div className="border-b border-stone-200 px-5 py-3 text-sm text-stone-500">
                  {sucursalesFiltradas.length === sucursales.length
                    ? `${sucursales.length} sucursales`
                    : `${sucursalesFiltradas.length} de ${sucursales.length} sucursales`}
                </div>
                {sucursalesFiltradas.length === 0 ? (
                  <div className="p-10 text-center text-sm text-stone-500">
                    {sucursales.length === 0 ? 'No hay sucursales registradas.' : 'Ninguna sucursal coincide con la búsqueda.'}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="ui-tabla tabla-apilada min-w-full text-left text-sm text-stone-600">
                      <thead className="bg-stone-50 text-xs uppercase tracking-[0.16em] text-stone-500">
                        <tr>
                          <th className="px-5 py-3 font-semibold">Sucursal</th>
                          <th className="px-5 py-3 font-semibold">Dirección</th>
                          <th className="px-5 py-3 font-semibold">Teléfono</th>
                          <th className="px-5 py-3 text-right font-semibold">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sucursalesFiltradas.map(sucursal => (
                          <tr key={sucursal.id} className="border-t border-stone-100 align-middle">
                            <td data-label="Sucursal" className="px-5 py-3">
                              <div className="flex items-center gap-3">
                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-stone-100 text-stone-600">
                                  <i className="fa-solid fa-building" aria-hidden="true" />
                                </span>
                                <span className="font-semibold text-stone-900">{sucursal.nombre}</span>
                              </div>
                            </td>
                            <td data-label="Dirección" className="px-5 py-3">{sucursal.direccion || '—'}</td>
                            <td data-label="Teléfono" className="px-5 py-3 whitespace-nowrap">{sucursal.telefono || '—'}</td>
                            <td data-label="" className="whitespace-nowrap px-5 py-3">
                              <div className="flex justify-end gap-1.5">
                                <button
                                  type="button"
                                  title="Editar sucursal"
                                  aria-label={`Editar ${sucursal.nombre}`}
                                  onClick={() => {
                                    setEditingSucursal(sucursal);
                                    setSucursalForm({
                                      nombre: sucursal.nombre,
                                      direccion: sucursal.direccion,
                                      telefono: sucursal.telefono,
                                    });
                                    setShowSucursalForm(false);
                                  }}
                                  className="ui-btn ui-btn-secondary ui-btn-sm"
                                >
                                  <i className="fa-solid fa-pen" aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  title="Eliminar sucursal"
                                  aria-label={`Eliminar ${sucursal.nombre}`}
                                  onClick={() => void handleEliminarSucursal(sucursal)}
                                  className="ui-btn ui-btn-danger ui-btn-sm"
                                >
                                  <i className="fa-solid fa-trash-can" aria-hidden="true" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </section>
          )}

          {section === 'bodega-proveedores' && (
            <BodegaProveedoresPanel />
          )}

          {section === 'bodega-insumos' && (
            <BodegaInsumosPanel />
          )}

          {section === 'bodega-asignaciones' && (
            <BodegaAsignacionesPanel />
          )}

          {section === 'gasolina-historial' && (
            <AdminGasolinaHistoryPanel conductores={conductores} vehiculos={vehiculos} />
          )}

          {section === 'mantenimientos' && (
            <MantenimientosPanel />
          )}

          {section === 'caja' && (
            <AdminCajaPanel />
          )}

          {section === 'reportes-kilometros' && (
            <AdminKilometrosPanel />
          )}

          {section === 'parametros' && (
            <section className="space-y-6">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-end">
                <div className="w-full max-w-sm">
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Buscar parámetro</label>
                  <input
                    value={parametrosBusqueda}
                    onChange={event => setParametrosBusqueda(event.target.value)}
                    placeholder="Slug, nombre, descripción o valor"
                    className="ui-input"
                  />
                </div>
              </div>

              <div className="grid gap-4 xl:grid-cols-3">
                <article className="ui-card p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Total</p>
                  <p className="mt-4 text-3xl font-semibold text-stone-950">{parametros.length}</p>
                </article>
                <article className="ui-card p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Públicos</p>
                  <p className="mt-4 text-3xl font-semibold text-stone-950">{parametros.filter(parametro => parametro.es_publico).length}</p>
                </article>
                <article className="ui-card p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Activos</p>
                  <p className="mt-4 text-3xl font-semibold text-stone-950">{parametros.filter(parametro => parametro.estado !== false).length}</p>
                </article>
              </div>

              {parametrosCargando ? (
                <SkeletonRows etiqueta="Cargando parámetros..." />
              ) : parametrosFiltrados.length === 0 ? (
                <div className="rounded-[var(--radius-card)] border border-dashed border-stone-300 bg-white/70 p-12 text-center text-stone-500">
                  No hay parámetros que coincidan con la búsqueda.
                </div>
              ) : (
                <SectionSurface className="overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="ui-tabla min-w-full border-separate border-spacing-y-2 text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-[0.18em] text-[var(--freisa-text-muted)]">
                          <th className="pb-2 pr-4 font-semibold">Slug</th>
                          <th className="pb-2 pr-4 font-semibold">Nombre</th>
                          <th className="pb-2 pr-4 font-semibold">Valor</th>
                          <th className="pb-2 pr-4 font-semibold">Tipo</th>
                          <th className="pb-2 pr-4 font-semibold">Visibilidad</th>
                          <th className="pb-2 pr-4 font-semibold">Estado</th>
                          <th className="pb-2 font-semibold">Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parametrosFiltrados.map(parametro => (
                          <tr key={parametro.id_parametro ?? parametro.slug} className="bg-[var(--freisa-bg)]/70 text-[var(--freisa-text)]">
                            <td className="rounded-l-[16px] px-4 py-3 font-semibold">{parametro.slug}</td>
                            <td className="px-4 py-3 text-[var(--freisa-text-soft)]">{parametro.nombre || '—'}</td>
                            <td className="max-w-[280px] px-4 py-3 text-[var(--freisa-text-soft)]">
                              <p className="line-clamp-2 break-words">{parametro.valor || '—'}</p>
                            </td>
                            <td className="px-4 py-3 text-[var(--freisa-text-soft)]">{parametro.tipo || '—'}</td>
                            <td className="px-4 py-3">
                              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${parametro.es_publico ? 'border border-emerald-200 bg-emerald-50 text-emerald-700' : 'border border-stone-200 bg-stone-100 text-stone-700'}`}>
                                {parametro.es_publico ? 'Público' : 'Privado'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${parametro.estado !== false ? 'border border-blue-200 bg-blue-50 text-blue-700' : 'border border-amber-200 bg-amber-50 text-amber-800'}`}>
                                {parametro.estado !== false ? 'Activo' : 'Inactivo'}
                              </span>
                            </td>
                            <td className="rounded-r-[16px] px-4 py-3">
                              <button
                                onClick={() => {
                                  setEditingParametro(parametro);
                                  setParametroValor(parametro.valor || '');
                                }}
                                className="ui-btn ui-btn-info ui-btn-sm"
                              >
                                Editar valor
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </SectionSurface>
              )}
            </section>
          )}

          {section === 'mapa' && (
            <section className="space-y-5">

              {conductoresActivos.length === 0 ? (
                <div className="rounded-[var(--radius-card)] border border-dashed border-stone-300 bg-white/70 p-12 text-center text-stone-500">
                  No hay conductores activos disponibles para seguimiento.
                </div>
              ) : !mapaConductorId ? (
                <div className="rounded-[var(--radius-card)] border border-white/80 bg-white/85 p-6 shadow-[0_24px_60px_rgba(120,113,108,0.12)]">
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
                    Selecciona un conductor
                  </label>
                  <select
                    value={mapaConductorId}
                    onChange={event => {
                      setMapaConductorId(event.target.value);
                      setMapaViaje(null);
                      setMapaError('');
                    }}
                    className="ui-input"
                  >
                    <option value="">Selecciona un conductor</option>
                    {conductoresActivos.map(conductor => (
                      <option key={conductor.id} value={conductor.id}>
                        {conductor.nombre}{conductor.placa ? ` · ${conductor.placa}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <>
                  <div className="flex flex-col gap-3 rounded-[var(--radius-card)] border border-white/80 bg-white/85 p-5 shadow-[0_24px_60px_rgba(120,113,108,0.12)] md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-lg font-semibold text-stone-900">{mapaConductor?.nombre || 'Conductor seleccionado'}</p>
                      <p className="text-sm text-stone-500">{mapaConductor?.placa || 'Sin placa asignada'} · Seguimiento individual en tiempo real</p>
                    </div>
                    <button
                      onClick={() => {
                        setMapaConductorId('');
                        setMapaViaje(null);
                        setMapaError('');
                      }}
                      className="ui-btn ui-btn-secondary"
                    >
                      Volver
                    </button>
                  </div>

                  {mapaError && (
                    <div className="rounded-[var(--radius-card)] border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                      {mapaError}
                    </div>
                  )}

                  {mapaCargando && !mapaViaje && (
                    <SkeletonRows etiqueta="Cargando ubicación del conductor..." />
                  )}

                  {!mapaCargando && !mapaViaje && !mapaError && (
                    <div className="rounded-[var(--radius-card)] border border-dashed border-stone-300 bg-white/70 p-12 text-center text-stone-500">
                      Este conductor no tiene un viaje en curso en este momento.
                    </div>
                  )}

                  {mapaViaje && (
                    <div className="rounded-[var(--radius-card)] border border-white/80 bg-white/85 p-5 shadow-[0_24px_60px_rgba(120,113,108,0.12)]">
                      <div className="mb-4 flex items-center justify-between gap-3">
                        <div>
                          <p className="text-lg font-semibold text-stone-900">{mapaConductor?.nombre || 'Conductor'}</p>
                          <p className="text-sm text-stone-500">{mapaConductor?.placa || 'Sin placa'} · {mapaViaje.paquete?.nombre || 'Sin paquete'}</p>
                        </div>
                        <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">En curso</span>
                      </div>
                      <MapaAdmin
                        key={mapaViaje.id}
                        coordenadas={mapaViaje.coordenadas}
                        coordenadaActual={mapaViaje.coordenadaActual}
                        coordenadaInicio={mapaViaje.coordenadaInicio}
                        puntosIntermedios={mapaViaje.puntosIntermedios}
                        conductorNombre={mapaConductor?.nombre}
                      />
                    </div>
                  )}
                </>
              )}
            </section>
          )}

          {section === 'historial' && (
            <section className="space-y-5">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-end">
                <p className="text-sm text-stone-500">{historialTotalRegistros} registros</p>
              </div>

              <div className="ui-card overflow-hidden">
                <div className="ui-filtros border-b border-stone-200 px-5 py-4">
                  <div className="ui-filtro-busqueda">
                    <label className="ui-filtro-label">Buscar historial</label>
                    <input
                      value={historialBusqueda}
                      onChange={event => setHistorialBusqueda(event.target.value)}
                      placeholder="Entrega, cliente, vendedor o conductor"
                      className="ui-input"
                    />
                  </div>
                  <div>
                    <label className="ui-filtro-label">Vendedor</label>
                    <select
                      value={historialVendedorFiltro}
                      onChange={event => setHistorialVendedorFiltro(event.target.value)}
                      className="ui-input"
                    >
                      <option value="todos">Todos</option>
                      {vendedoresConHistorial.map(vendedor => (
                        <option key={vendedor.id} value={String(vendedor.id)}>
                          {vendedor.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="ui-filtro-label">Conductor</label>
                    <select
                      value={historialConductorFiltro}
                      onChange={event => setHistorialConductorFiltro(event.target.value)}
                      className="ui-input"
                    >
                      <option value="todos">Todos</option>
                      {conductoresConHistorial.map(conductor => (
                        <option key={conductor.id} value={String(conductor.id)}>
                          {conductor.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="ui-filtro-label">Tipo de entrega</label>
                    <select
                      value={historialTipoEntrega}
                      onChange={event => setHistorialTipoEntrega(event.target.value as typeof historialTipoEntrega)}
                      className="ui-input"
                    >
                      <option value="todas">Todas</option>
                      <option value="entrega">Entrega</option>
                      <option value="envio">Envío</option>
                      <option value="transferencia">Transferencia</option>
                    </select>
                  </div>
                  <div>
                    <label className="ui-filtro-label">Estado</label>
                    <select
                      value={historialEstadoFiltro}
                      onChange={event => setHistorialEstadoFiltro(event.target.value as typeof historialEstadoFiltro)}
                      className="ui-input"
                    >
                      <option value="todos">Todos</option>
                      <option value="pendiente">No iniciado</option>
                      <option value="en_curso">En curso</option>
                      <option value="completado">Finalizado</option>
                    </select>
                  </div>
                  <div>
                    <label className="ui-filtro-label">Devolución</label>
                    <select
                      value={historialDevolucionFiltro}
                      onChange={event => setHistorialDevolucionFiltro(event.target.value as typeof historialDevolucionFiltro)}
                      className="ui-input"
                    >
                      <option value="todas">Todas</option>
                      <option value="si">Solo devoluciones</option>
                      <option value="no">Sin devoluciones</option>
                    </select>
                  </div>
                  <div>
                    <label className="ui-filtro-label">Fecha inicio</label>
                    <input
                      type="date"
                      value={historialFechaInicio}
                      onChange={event => setHistorialFechaInicio(event.target.value)}
                      className="ui-input"
                    />
                  </div>
                  <div>
                    <label className="ui-filtro-label">Fecha fin</label>
                    <input
                      type="date"
                      value={historialFechaFin}
                      onChange={event => setHistorialFechaFin(event.target.value)}
                      className="ui-input"
                    />
                  </div>
                </div>

                {historialCargando && (
                  <BarraProgreso etiqueta="Actualizando historial..." />
                )}

                {historialTotalRegistros === 0 && !historialCargando ? (
                  <div className="px-5 py-12 text-center text-stone-500">No hay viajes que coincidan con el filtro aplicado.</div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="ui-tabla tabla-apilada min-w-full text-left text-sm text-stone-600">
                        <thead className="bg-stone-50 text-xs uppercase tracking-[0.16em] text-stone-500">
                          <tr>
                            <th className="px-5 py-3 font-semibold">Fecha de creación</th>
                            <th className="px-5 py-3 font-semibold">Entrega</th>
                            <th className="px-5 py-3 font-semibold">Vendedor</th>
                            <th className="px-5 py-3 font-semibold">Conductor</th>
                            <th className="px-5 py-3 font-semibold">Ubicación destino</th>
                            <th className="px-5 py-3 font-semibold">Tipo</th>
                            <th className="px-5 py-3 font-semibold">Estado</th>
                            <th className="px-5 py-3 font-semibold">Número de factura</th>
                            <th className="px-5 py-3 font-semibold">Costo viaje</th>
                            <th className="px-5 py-3 font-semibold text-right">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {historialViajes.map(viaje => {
                            const conductor = conductores.find(item => item.id === viaje.conductorId);
                            const conductorNombre = conductor?.nombre || viaje.conductorNombre;
                            const costoViaje = getTripBaseCost(viaje, tarifaPorKilometro);
                            const ubicacionDestino = getViajeDestinationUrl(viaje);
                            const invoiceNumbers = getViajeInvoiceNumbers(viaje);
                            return (
                              <tr key={viaje.id} className="border-t border-stone-100 align-top">
                                <td data-label="Fecha de creación" className="px-5 py-4 whitespace-nowrap text-stone-700">{formatShortDateTime(viaje.createdAt)}</td>
                                <td data-label="Entrega" className="px-5 py-4 font-semibold text-stone-900">{viaje.paquete?.nombre || 'Sin paquete'}</td>
                                <td data-label="Vendedor" className="px-5 py-4">{viaje.vendedor?.nombre || viaje.vendedor?.username || '—'}{viaje.vendedor?.esErp && <span title="Vendedor sincronizado desde el ERP" className="ml-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 align-middle text-[10px] font-bold text-indigo-700">ERP</span>}</td>
                                <td data-label="Conductor" className="px-5 py-4">{conductorNombre || '—'}</td>
                                <td data-label="Ubicación destino" className="px-5 py-4">
                                  <div className="max-w-[240px] whitespace-normal break-all leading-5 text-stone-700">
                                    {ubicacionDestino ? (
                                      <a
                                        href={ubicacionDestino}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="font-medium text-blue-700 underline decoration-blue-200 underline-offset-4 transition hover:text-blue-900"
                                      >
                                        {ubicacionDestino}
                                      </a>
                                    ) : '—'}
                                  </div>
                                </td>
                                <td data-label="Tipo" className="px-5 py-4">{tipoEntregaLabel(viaje.tipoEntrega)}</td>
                                <td data-label="Estado" className="px-5 py-4">
                                  <span className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold ${estadoViajeClases(viaje.estado)}`}>
                                    {estadoViajeLabel(viaje.estado)}
                                  </span>
                                </td>
                                <td data-label="Número de factura" className="px-5 py-4">
                                  {invoiceNumbers.length > 0 ? (
                                    <div className="flex max-w-[260px] flex-wrap gap-2">
                                      {invoiceNumbers.map((invoiceNumber, index) => (
                                        <span
                                          key={`${viaje.id}-historial-invoice-${index}`}
                                          className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-700"
                                        >
                                          {invoiceNumber}
                                        </span>
                                      ))}
                                    </div>
                                  ) : '—'}
                                </td>
                                <td data-label="Costo viaje" className="px-5 py-4">{typeof costoViaje === 'number' ? formatCurrency(costoViaje) : '—'}</td>
                                <td data-label="" className="px-5 py-4 text-right">
                                  <button
                                    onClick={() => void handleVerDetalleViaje(viaje)}
                                    className="ui-btn ui-btn-info ui-btn-sm"
                                  >
                                    Ver detalles
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-stone-200 px-5 py-4 text-sm text-stone-500">
                      <p>
                        Mostrando {Math.min((historialPaginaActual - 1) * HISTORIAL_PAGE_SIZE + 1, historialTotalRegistros)}-
                        {Math.min(historialPaginaActual * HISTORIAL_PAGE_SIZE, historialTotalRegistros)} de {historialTotalRegistros}
                      </p>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setHistorialPagina(current => Math.max(1, current - 1))}
                          disabled={historialPaginaActual === 1}
                          className="rounded-2xl border border-stone-200 px-3 py-2 font-medium text-stone-700 transition hover:bg-stone-100 disabled:opacity-50"
                        >
                          Anterior
                        </button>
                        <span>Página {historialPaginaActual} de {historialTotalPaginas}</span>
                        <button
                          type="button"
                          onClick={() => setHistorialPagina(current => Math.min(historialTotalPaginas, current + 1))}
                          disabled={historialPaginaActual === historialTotalPaginas}
                          className="rounded-2xl border border-stone-200 px-3 py-2 font-medium text-stone-700 transition hover:bg-stone-100 disabled:opacity-50"
                        >
                          Siguiente
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </section>
          )}
      </PanelShell>
      {(showUserForm || editingUser) && (
        <AdminFormPopup
          onClose={() => {
            setShowUserForm(false);
            setEditingUser(null);
          }}
        >
          <FormUsuario
            initialUser={editingUser}
            onSaved={() => {
              setShowUserForm(false);
              setEditingUser(null);
              void refresh();
            }}
            onCancel={() => {
              setShowUserForm(false);
              setEditingUser(null);
            }}
          />
        </AdminFormPopup>
      )}
      {(showVehiculoForm || editingVehiculo) && (
        <AdminFormPopup
          maxWidthClassName="max-w-4xl"
          onClose={() => {
            setEditingVehiculo(null);
            setShowVehiculoForm(false);
          }}
        >
          <FormVehiculo
            initialVehiculo={editingVehiculo}
            onSaved={() => {
              setEditingVehiculo(null);
              setShowVehiculoForm(false);
              void refresh();
            }}
            onCancel={() => {
              setEditingVehiculo(null);
              setShowVehiculoForm(false);
            }}
          />
        </AdminFormPopup>
      )}
      {editingViaje && (
        <AdminFormPopup
          maxWidthClassName="max-w-5xl"
          onClose={() => setEditingViaje(null)}
        >
          <AdminEditViajeForm
            initialViaje={editingViaje}
            sucursales={sucursales}
            onSaved={async viajeActualizado => {
              setDetalleViaje(current => (current?.id === viajeActualizado.id ? viajeActualizado : current));
              setEditingViaje(null);
              await refresh();
            }}
            onCancel={() => setEditingViaje(null)}
          />
        </AdminFormPopup>
      )}
      {asignacionViajeActual && (
        <AdminFormPopup
          maxWidthClassName="max-w-4xl"
          onClose={() => setViajeEnAsignacion(null)}
        >
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-stone-950">{asignacionViajeActual.esTransferencia ? 'Asignar transferencia' : 'Asignar conductor'}</h3>
              <p className="mt-1 text-sm text-stone-500">{asignacionViajeActual.viaje.paquete?.nombre || 'Entrega sin nombre'} · {asignacionViajeActual.esTransferencia ? 'selecciona los dos conductores y sus vehículos antes de confirmar.' : 'selecciona el conductor y su vehículo antes de confirmar.'}</p>
            </div>

            <div className={`grid gap-4 ${asignacionViajeActual.esTransferencia ? 'md:grid-cols-2' : ''}`}>
              <div className="rounded-[var(--radius-card)] border border-stone-200 bg-stone-50/70 p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">{asignacionViajeActual.esTransferencia ? 'Conductor principal' : 'Conductor asignado'}</p>
                <p className="mt-2 text-sm text-stone-600">Actual: {asignacionViajeActual.conductorActual ? `${asignacionViajeActual.conductorActual.nombre} · ${formatVehiculoResumen(asignacionViajeActual.vehiculoActual)}` : 'Sin asignar'}</p>
                <div className="mt-4 space-y-3">
                  <select
                    value={asignacionViajeActual.conductorSeleccionadoId}
                    onChange={event => {
                      const nextConductorId = event.target.value;
                      setSeleccionConductorPorViaje(prev => ({ ...prev, [asignacionViajeActual.viaje.id]: nextConductorId }));
                      setSeleccionVehiculoPorViaje(prev => ({ ...prev, [asignacionViajeActual.viaje.id]: '' }));
                    }}
                    className="ui-input"
                  >
                    <option value="">Selecciona un conductor</option>
                    {asignacionViajeActual.conductoresAsignables.map(conductor => (
                      <option key={conductor.id} value={conductor.id}>
                        {formatConductorOptionLabel(conductor, openTripCountsByConductor)}
                      </option>
                    ))}
                  </select>
                  <select
                    value={asignacionViajeActual.vehiculoSeleccionadoId}
                    onChange={event => setSeleccionVehiculoPorViaje(prev => ({ ...prev, [asignacionViajeActual.viaje.id]: event.target.value }))}
                    disabled={!asignacionViajeActual.conductorSeleccionadoId}
                    className="ui-input"
                  >
                    <option value="">Selecciona un vehículo</option>
                    {asignacionViajeActual.vehiculosAsignados.map(vehiculo => (
                      <option key={vehiculo.id} value={vehiculo.id} disabled={Boolean(vehiculo.etiqueta)}>
                        {formatVehiculoOptionLabel(vehiculo)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {asignacionViajeActual.esTransferencia && (
              <div className="rounded-[var(--radius-card)] border border-stone-200 bg-stone-50/70 p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Conductor secundario</p>
                <p className="mt-2 text-sm text-stone-600">Actual: {asignacionViajeActual.conductorSecundarioActual ? `${asignacionViajeActual.conductorSecundarioActual.nombre} · ${formatVehiculoResumen(asignacionViajeActual.vehiculoSecundarioActual)}` : 'Sin asignar'}</p>
                <div className="mt-4 space-y-3">
                  <select
                    value={asignacionViajeActual.conductorSecundarioSeleccionadoId}
                    onChange={event => {
                      const nextConductorId = event.target.value;
                      setSeleccionConductorSecundarioPorViaje(prev => ({ ...prev, [asignacionViajeActual.viaje.id]: nextConductorId }));
                      setSeleccionVehiculoSecundarioPorViaje(prev => ({ ...prev, [asignacionViajeActual.viaje.id]: '' }));
                    }}
                    className="ui-input"
                  >
                    <option value="">Selecciona un segundo conductor</option>
                    {asignacionViajeActual.conductoresSecundariosAsignables.map(conductor => (
                      <option key={`sec-${conductor.id}`} value={conductor.id}>
                        {formatConductorOptionLabel(conductor, openTripCountsByConductor)}
                      </option>
                    ))}
                  </select>
                  <select
                    value={asignacionViajeActual.vehiculoSecundarioSeleccionadoId}
                    onChange={event => setSeleccionVehiculoSecundarioPorViaje(prev => ({ ...prev, [asignacionViajeActual.viaje.id]: event.target.value }))}
                    disabled={!asignacionViajeActual.conductorSecundarioSeleccionadoId}
                    className="ui-input"
                  >
                    <option value="">Selecciona el segundo vehículo</option>
                    {asignacionViajeActual.vehiculosSecundariosAsignados.map(vehiculo => (
                      <option key={`sec-veh-${vehiculo.id}`} value={vehiculo.id} disabled={Boolean(vehiculo.etiqueta)}>
                        {formatVehiculoOptionLabel(vehiculo)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              )}
            </div>

            <div className="flex flex-wrap justify-end gap-3">
              <button
                type="button"
                onClick={() => setViajeEnAsignacion(null)}
                className="ui-btn ui-btn-secondary"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void handleAsignarViaje(asignacionViajeActual.viaje)}
                disabled={
                  asignandoViajeId === asignacionViajeActual.viaje.id
                  || !asignacionViajeActual.conductorSeleccionadoId
                  || !asignacionViajeActual.vehiculoSeleccionadoId
                  || (asignacionViajeActual.esTransferencia && !asignacionViajeActual.conductorSecundarioSeleccionadoId)
                  || (asignacionViajeActual.esTransferencia && !asignacionViajeActual.vehiculoSecundarioSeleccionadoId)
                }
                className="ui-btn ui-btn-primary"
              >
                {asignandoViajeId === asignacionViajeActual.viaje.id ? 'Asignando...' : 'Confirmar asignación'}
              </button>
            </div>
          </div>
        </AdminFormPopup>
      )}
      {(showSucursalForm || editingSucursal) && (
        <Modal
          onClose={() => {
            setEditingSucursal(null);
            setShowSucursalForm(false);
            resetSucursalForm();
          }}
          maxWidthClassName="max-w-2xl"
          ariaLabel={editingSucursal ? 'Editar sucursal' : 'Nueva sucursal'}
        >
          <div className="p-6">
            <h3 className="pr-10 text-xl font-semibold text-stone-950">{editingSucursal ? 'Editar sucursal' : 'Nueva sucursal'}</h3>
            <p className="mt-1 text-sm text-stone-500">Los vendedores eligen esta sucursal al crear entregas.</p>
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Nombre</label>
                <input
                  autoFocus
                  value={sucursalForm.nombre}
                  onChange={event => setSucursalForm(prev => ({ ...prev, nombre: event.target.value }))}
                  className="ui-input"
                  placeholder="Sucursal Norte"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Dirección</label>
                <input
                  value={sucursalForm.direccion}
                  onChange={event => setSucursalForm(prev => ({ ...prev, direccion: event.target.value }))}
                  className="ui-input"
                  placeholder="Av. Principal y Calle 10"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Teléfono</label>
                <input
                  value={sucursalForm.telefono}
                  onChange={event => setSucursalForm(prev => ({ ...prev, telefono: event.target.value }))}
                  className="ui-input"
                  placeholder="0999999999"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <ModalCloseButton className="ui-btn ui-btn-secondary">Cancelar</ModalCloseButton>
              <button
                type="button"
                onClick={() => void handleGuardarSucursal()}
                disabled={savingSucursal}
                className="ui-btn ui-btn-primary"
              >
                {savingSucursal ? 'Guardando...' : editingSucursal ? 'Actualizar sucursal' : 'Crear sucursal'}
              </button>
            </div>
          </div>
        </Modal>
      )}
      {detalleViaje && (
        <Modal onClose={() => setDetalleViaje(null)} maxWidthClassName="max-w-4xl" showCloseButton={false}>
          <div className="sticky top-0 flex items-center justify-between border-b border-stone-200 bg-white px-6 py-4">
            <div>
              <h3 className="text-lg font-semibold text-stone-950">Detalle de la entrega</h3>
              <p className="text-sm text-stone-500">{detalleViaje.paquete?.nombre || 'Entrega sin nombre'} · {conductorDetalle?.nombre || detalleViaje.vendedor?.nombre || 'Sin conductor asignado'}</p>
            </div>
            <ModalCloseButton
              className="ui-btn ui-btn-secondary ui-btn-sm"
            >
              Cerrar
            </ModalCloseButton>
          </div>

          <div className="space-y-6 px-6 py-5">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-[var(--radius-card)] border border-stone-200 p-4">
                <h4 className="mb-3 font-semibold text-stone-900">Paquete</h4>
                <div className="space-y-2 text-sm text-stone-600">
                  <p>Nombre: {detalleViaje.paquete?.nombre || '—'}</p>
                  <p>Descripción: {detalleViaje.paquete?.descripcion || '—'}</p>
                  <p>Tipo de entrega: {tipoEntregaLabel(detalleViaje.tipoEntrega)}</p>
                  <p>Valor total factura: {typeof detalleViaje.paquete?.valorTotalFactura === 'number' ? formatCurrency(detalleViaje.paquete.valorTotalFactura) : '—'}</p>
                  <p>Tiempo estimado: {detalleViaje.tiempoEstimadoMinutos ? `${detalleViaje.tiempoEstimadoMinutos} min` : '—'}</p>
                  <p>
                    PDF de envío: {detalleEnvioDocumento ? (
                      <a
                        href={detalleEnvioDocumento.url}
                        target="_blank"
                        rel="noreferrer"
                        className="font-medium text-blue-700 underline decoration-blue-200 underline-offset-4 transition hover:text-blue-900"
                      >
                        {detalleEnvioDocumento.nombreArchivo}
                      </a>
                    ) : '—'}
                  </p>
                  <p>
                    PDF guía: {detalleGuiaPrincipal ? (
                      <a
                        href={detalleGuiaPrincipal.url}
                        target="_blank"
                        rel="noreferrer"
                        className="font-medium text-blue-700 underline decoration-blue-200 underline-offset-4 transition hover:text-blue-900"
                      >
                        {detalleGuiaPrincipal.nombreArchivo}
                      </a>
                    ) : '—'}
                  </p>
                  {detalleViaje.tipoEntrega === 'envio' && <p>Compañía de transporte: {detalleViaje.companiaTransporte || '—'}</p>}
                  {detalleViaje.tipoEntrega === 'envio' && <p>Valor total de envío: {typeof detalleViaje.valorTotalEnvio === 'number' ? formatCurrency(detalleViaje.valorTotalEnvio) : '—'}</p>}
                </div>
              </div>

              <div className="rounded-[var(--radius-card)] border border-stone-200 p-4">
                <h4 className="mb-3 font-semibold text-stone-900">Cliente</h4>
                <div className="space-y-2 text-sm text-stone-600">
                  <p>Nombre: {detalleViaje.paquete?.cliente?.nombre || '—'}</p>
                  <p>Correo: {detalleViaje.paquete?.cliente?.correoElectronico || '—'}</p>
                  <p>Teléfono: {detalleViaje.paquete?.cliente?.telefono || '—'}</p>
                  <p>Dirección: {detalleViaje.paquete?.cliente?.direccion || detalleViaje.direccionEntrega || '—'}</p>
                  <p>Ciudad: {detalleViaje.paquete?.cliente?.ciudad || '—'}</p>
                  <p>Sucursal del cliente: {detalleViaje.paquete?.cliente?.sucursal || '—'}</p>
                </div>
              </div>

              <div className="rounded-[var(--radius-card)] border border-stone-200 p-4">
                <h4 className="mb-3 font-semibold text-stone-900">Forma de pago acordada</h4>
                <div className="space-y-2 text-sm text-stone-600">
                  {(() => {
                    const pago = detalleViaje.paquete?.pago;
                    const total = detalleViaje.paquete?.valorTotalFactura;
                    const porCobrar = getAmountToCollect(pago, total);
                    if (!pago) return <p>Sin forma de pago acordada</p>;
                    if (pago.tipo === 'completo') {
                      return (
                        <>
                          <p>Tipo: PAGADO (el conductor no cobra)</p>
                          <p>Monto pagado: {typeof pago.monto === 'number' ? formatCurrency(pago.monto) : '—'}</p>
                        </>
                      );
                    }
                    return (
                      <>
                        <p>Tipo: {pago.tipo === 'parcial' ? 'Pago parcial (POR COBRAR el saldo)' : `${formatPaymentLabel(pago.tipo)} (POR COBRAR)`}</p>
                        {pago.tipo === 'parcial' && (
                          <p>Pagado (anticipo): {typeof pago.montoPagado === 'number' ? formatCurrency(pago.montoPagado) : '—'}</p>
                        )}
                        <p className="font-semibold text-stone-900">
                          {pago.tipo === 'parcial' ? 'Saldo por cobrar' : 'Monto por cobrar'}: {typeof porCobrar === 'number' ? formatCurrency(porCobrar) : '—'}
                        </p>
                        {pago.tipo === 'parcial' && <p>Medio acordado del saldo: {formatPaymentLabel(pago.tipoPagoParcial)}</p>}
                        {pago.banco && <p>Banco: {pago.banco}</p>}
                        {pago.numeroTransferencia && <p>Transferencia: {pago.numeroTransferencia}</p>}
                        {pago.numeroCheque && <p>Cheque: {pago.numeroCheque}</p>}
                        {pago.numeroNotaCredito && <p>Nota de crédito: {pago.numeroNotaCredito}</p>}
                      </>
                    );
                  })()}
                </div>
              </div>

              {(detalleViaje.estado === 'finalizado' || detalleViaje.pagoEntrega) && (
                <div className="rounded-[var(--radius-card)] border border-stone-200 p-4">
                  <h4 className="mb-3 font-semibold text-stone-900">Cobro registrado por el conductor</h4>
                  <div className="space-y-2 text-sm text-stone-600">
                    {(() => {
                      const cobro = detalleViaje.pagoEntrega;
                      if (!cobro?.tipo) {
                        return <p>{detalleViaje.esDevolucion ? 'Devolución sin cobro.' : 'El conductor no registró un cobro.'}</p>;
                      }
                      const monto = cobro.tipo === 'nota_credito' ? cobro.valorNotaCredito : cobro.monto;
                      const acordado = detalleViaje.paquete?.pago;
                      const porCobrar = getAmountToCollect(acordado, detalleViaje.paquete?.valorTotalFactura);
                      const cobrado = cobro.tipo === 'completo' ? 0 : (monto ?? 0);
                      const diferencia = typeof porCobrar === 'number' ? Number((porCobrar - cobrado).toFixed(2)) : undefined;
                      return (
                        <>
                          <p>Forma: {cobro.tipo === 'completo' ? 'Pagado (no se cobró)' : formatPaymentLabel(cobro.tipo)}</p>
                          {cobro.tipo !== 'completo' && (
                            <p className="font-semibold text-stone-900">Monto cobrado: {typeof monto === 'number' ? formatCurrency(monto) : '—'}</p>
                          )}
                          {cobro.banco && <p>Banco: {cobro.banco}</p>}
                          {cobro.numeroTransferencia && <p>Transferencia: {cobro.numeroTransferencia}</p>}
                          {cobro.numeroCheque && <p>Cheque: {cobro.numeroCheque}</p>}
                          {cobro.numeroNotaCredito && <p>Nota de crédito: {cobro.numeroNotaCredito}</p>}
                          {detalleViaje.esDevolucion && <p>Con devolución de mercadería.</p>}
                          {typeof diferencia === 'number' && acordado?.tipo !== 'completo' && (
                            <p className={diferencia === 0 ? 'text-emerald-700' : 'font-semibold text-amber-700'}>
                              {diferencia === 0
                                ? 'Coincide con lo acordado.'
                                : diferencia > 0
                                  ? detalleViaje.esDevolucion
                                    ? `Diferencia de ${formatCurrency(diferencia)} frente a lo acordado (incluye la mercadería devuelta).`
                                    : `Falta cobrar ${formatCurrency(diferencia)} frente a lo acordado.`
                                  : `Se cobró ${formatCurrency(Math.abs(diferencia))} de más frente a lo acordado.`}
                            </p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}

              <div className="rounded-[var(--radius-card)] border border-stone-200 p-4">
                <h4 className="mb-3 font-semibold text-stone-900">Vendedor</h4>
                <div className="space-y-2 text-sm text-stone-600">
                  <p>Nombre: {detalleViaje.vendedor?.nombre || '—'}{detalleViaje.vendedor?.esErp && <span title="Vendedor sincronizado desde el ERP" className="ml-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 align-middle text-[10px] font-bold text-indigo-700">ERP</span>}</p>
                  <p>Usuario: {detalleViaje.vendedor?.username || '—'}</p>
                  {detalleViaje.vendedor?.esErp && <p>Origen: vendedor del ERP (sin acceso al GPS)</p>}
                  <p>Correo: {detalleViaje.vendedor?.correo || '—'}</p>
                  <p>Sucursal: {detalleViaje.vendedor?.sucursal?.nombre || detalleViaje.sucursal?.nombre || '—'}</p>
                </div>
              </div>

              <div className="rounded-[var(--radius-card)] border border-stone-200 p-4">
                <h4 className="mb-3 font-semibold text-stone-900">Conductor</h4>
                <div className="space-y-2 text-sm text-stone-600">
                  <p>Nombre: {conductorDetalle?.nombre || '—'}</p>
                  <p>Identificación: {conductorDetalle?.identificacion || '—'}</p>
                  <p>Teléfono: {conductorDetalle?.telefono || '—'}</p>
                  <p>Correo: {conductorDetalle?.correo || '—'}</p>
                  <p>Placa: {conductorDetalle?.placa || '—'}</p>
                </div>
              </div>

              <div className="rounded-[var(--radius-card)] border border-stone-200 p-4">
                <h4 className="mb-3 font-semibold text-stone-900">Entrega</h4>
                <div className="space-y-2 text-sm text-stone-600">
                  <p>Estado: {detalleViaje.esDevolucion ? 'Devolución' : detalleViaje.estado === 'pendiente' ? 'Pendiente' : detalleViaje.estado === 'en_curso' ? 'En curso' : 'Finalizado'}</p>
                  <p>Sucursal: {detalleViaje.sucursal?.nombre || detalleViaje.vendedor?.sucursal?.nombre || '—'}</p>
                  <p>Dirección final: {detalleViaje.direccionEntrega || '—'}</p>
                  <p>Puntos intermedios: {detalleViaje.puntosIntermedios.length}</p>
                  <p>Km recorridos: {detalleViaje.kilometrosRecorridos ?? '—'}</p>
                  <p>Valor total viaje: {typeof getTripTotal(detalleViaje) === 'number' ? formatCurrency(getTripTotal(detalleViaje)!) : '—'}</p>
                  <p>Costo viaje por km: {typeof getTripBaseCost(detalleViaje, tarifaPorKilometro) === 'number' ? formatCurrency(getTripBaseCost(detalleViaje, tarifaPorKilometro)!) : '—'}</p>
                  <p>Finalizado: {detalleViaje.fechaFin ? new Date(detalleViaje.fechaFin).toLocaleString() : '—'}</p>
                  {detalleViaje.gasolina && <p>Gasolina: {detalleViaje.gasolina.tipo} · {detalleViaje.gasolina.galones} gal · ${(detalleViaje.gasolina.total ?? detalleViaje.gasolina.galones * detalleViaje.gasolina.precioPorGalon).toFixed(2)}</p>}
                  {detalleViaje.observaciones && <p>Observaciones: {detalleViaje.observaciones}</p>}
                </div>
              </div>
            </div>

            {(detalleViaje.coordenadas.length > 0 || detalleViaje.coordenadaInicio || detalleViaje.coordenadaFin || detalleViaje.puntosIntermedios.length > 0) && (
              <div className="rounded-[var(--radius-card)] border border-stone-200 p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h4 className="font-semibold text-stone-900">Mapa y recorrido</h4>
                  <button
                    onClick={() => setHistorialMapaViaje(detalleViaje)}
                    className="ui-btn ui-btn-success ui-btn-sm"
                  >
                    Abrir mapa grande
                  </button>
                </div>
                <MapaAdmin
                  key={`detalle-${detalleViaje.id}`}
                  coordenadas={detalleViaje.coordenadas}
                  coordenadaActual={detalleViaje.coordenadaActual}
                  coordenadaInicio={detalleViaje.coordenadaInicio}
                  coordenadaFin={detalleViaje.coordenadaFin}
                  puntosIntermedios={detalleViaje.puntosIntermedios}
                  conductorNombre={conductorDetalle?.nombre}
                />
              </div>
            )}

            <div className="rounded-[var(--radius-card)] border border-stone-200 p-4">
              <h4 className="mb-3 font-semibold text-stone-900">{detalleGuias.title}</h4>
              {detalleGuias.guias.length > 0 ? (
                <div className="flex flex-wrap gap-2 text-sm">
                  {detalleGuias.guias.map((guia, index) => (
                    <a
                      key={`${detalleViaje.id}-guia-${index}`}
                      href={guia.url}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-full bg-emerald-50 px-3 py-1.5 font-medium text-emerald-700 transition hover:bg-emerald-100"
                    >
                      {guia.numeroFactura ? `Factura ${guia.numeroFactura}` : guia.nombreArchivo || `Guía ${index + 1}`}
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-stone-500">No hay guías disponibles.</p>
              )}
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <div className="rounded-[var(--radius-card)] border border-stone-200 p-4">
                <h4 className="mb-3 font-semibold text-stone-900">Fotos del paquete</h4>
                {(detalleViaje.paquete?.imagenes?.length ?? 0) > 0 ? (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {detalleViaje.paquete?.imagenes?.map((image, index) => (
                      <a key={index} href={image} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-[var(--radius-panel)] border border-stone-200 bg-stone-50">
                        <img src={image} alt={`Paquete ${index + 1}`} className="h-56 w-full object-cover" loading="lazy" />
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-stone-500">No hay imágenes del paquete.</p>
                )}
              </div>

              <div className="rounded-[var(--radius-card)] border border-stone-200 p-4">
                <h4 className="mb-3 font-semibold text-stone-900">Fotos de entrega</h4>
                {(detalleViaje.imagenesEntrega?.length ? detalleViaje.imagenesEntrega : detalleViaje.imagenEntrega ? [detalleViaje.imagenEntrega] : []).length > 0 ? (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {(detalleViaje.imagenesEntrega?.length ? detalleViaje.imagenesEntrega : detalleViaje.imagenEntrega ? [detalleViaje.imagenEntrega] : []).map((image, index) => (
                      <a key={`${detalleViaje.id}-entrega-${index}`} href={image} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-[var(--radius-panel)] border border-emerald-200 bg-emerald-50">
                        <img src={image} alt={`Entrega ${index + 1}`} className="h-56 w-full object-cover" loading="lazy" />
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-stone-500">No hay imágenes de entrega.</p>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}
      {vinculandoErp && (
        <VincularVendedorErpModal
          vendedorErp={vinculandoErp}
          usuarios={usuarios}
          onClose={() => setVinculandoErp(null)}
          onGuardado={refresh}
        />
      )}
      {resumenAsignacion && (
        <Modal onClose={() => setResumenAsignacion(null)} maxWidthClassName="max-w-md" showCloseButton={false}>
          <div className="border-b border-stone-200 px-6 py-4">
            <h3 className="text-lg font-semibold text-stone-950">Entrega asignada</h3>
            <p className="text-sm text-stone-500">{resumenAsignacion.viajeNombre} · la asignación se guardó correctamente.</p>
          </div>
          <div className="space-y-4 px-6 py-5 text-sm text-stone-700">
            {resumenAsignacion.asignaciones.map(asignacion => (
              <div key={asignacion.titulo} className="rounded-[var(--radius-panel)] border border-stone-200 bg-stone-50 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">{asignacion.titulo}</p>
                <p className="mt-2 font-semibold text-stone-900">{asignacion.conductorNombre}</p>
                <p className="text-sm text-stone-600">{asignacion.vehiculoNombre}</p>
              </div>
            ))}
            <p>{resumenAsignacion.asignaciones.length > 1 ? 'La transferencia quedó asignada a los dos conductores seleccionados.' : 'La entrega quedó asignada al conductor seleccionado.'}</p>
            <div className="flex justify-end">
              <ModalCloseButton
                className="ui-btn ui-btn-primary"
              >
                Cerrar
              </ModalCloseButton>
            </div>
          </div>
        </Modal>
      )}
      {historialMapaViaje && (
        <Modal onClose={() => setHistorialMapaViaje(null)} maxWidthClassName="max-w-5xl" showCloseButton={false}>
          <div className="sticky top-0 flex items-center justify-between border-b border-stone-200 bg-white px-6 py-4">
            <div>
              <h3 className="text-lg font-semibold text-stone-950">Recorrido del viaje</h3>
              <p className="text-sm text-stone-500">{conductores.find(conductor => conductor.id === historialMapaViaje.conductorId)?.nombre || historialMapaViaje.conductorNombre || 'Conductor'} · {historialMapaViaje.paquete?.nombre || 'Sin paquete'}</p>
            </div>
            <ModalCloseButton
              className="ui-btn ui-btn-secondary ui-btn-sm"
            >
              Cerrar
            </ModalCloseButton>
          </div>

          <div className="space-y-4 px-6 py-5">
            <MapaAdmin
              key={`historial-${historialMapaViaje.id}`}
              coordenadas={historialMapaViaje.coordenadas}
              coordenadaActual={historialMapaViaje.coordenadaActual}
              coordenadaInicio={historialMapaViaje.coordenadaInicio}
              coordenadaFin={historialMapaViaje.coordenadaFin}
              puntosIntermedios={historialMapaViaje.puntosIntermedios}
              conductorNombre={conductores.find(conductor => conductor.id === historialMapaViaje.conductorId)?.nombre || historialMapaViaje.conductorNombre}
            />
            <div className="grid gap-2 text-sm text-stone-600 md:grid-cols-2">
              <p>Coordenadas registradas: {historialMapaViaje.coordenadas.length}</p>
              <p>Puntos intermedios: {historialMapaViaje.puntosIntermedios.length}</p>
              <p>Inicio: {historialMapaViaje.fechaInicio ? new Date(historialMapaViaje.fechaInicio).toLocaleString() : '—'}</p>
              <p>Fin: {historialMapaViaje.fechaFin ? new Date(historialMapaViaje.fechaFin).toLocaleString() : '—'}</p>
            </div>
          </div>
        </Modal>
      )}
      {editingParametro && (
        <Modal onClose={() => { setEditingParametro(null); setParametroValor(''); }} maxWidthClassName="max-w-2xl" showCloseButton={false}>
          <div className="flex items-center justify-between border-b border-stone-200 px-6 py-4">
            <div>
              <h3 className="text-lg font-semibold text-stone-950">Editar valor del parámetro</h3>
              <p className="text-sm text-stone-500">Solo se actualizará el valor del parámetro seleccionado.</p>
            </div>
            <ModalCloseButton
              className="ui-btn ui-btn-secondary ui-btn-sm"
            >
              Cerrar
            </ModalCloseButton>
          </div>

          <div className="space-y-5 px-6 py-5">
            {(editingParametro.slug === 'terminos_y_condiciones_url' || editingParametro.slug === 'sistema_logo_url' || editingParametro.slug === 'logo_factura_url') && (
              <div className="rounded-[var(--radius-card)] border border-blue-200 bg-blue-50/70 p-4 text-sm text-blue-800">
                <p className="font-semibold text-blue-900">
                  {editingParametro.slug === 'terminos_y_condiciones_url'
                    ? 'Documento de términos y condiciones'
                    : editingParametro.slug === 'logo_factura_url'
                    ? 'Logo de factura'
                    : 'Logo del sistema'}
                </p>
                <p className="mt-2">
                  {editingParametro.slug === 'sistema_logo_url' || editingParametro.slug === 'logo_factura_url'
                    ? 'Sube aquí la imagen del logo para guardar automáticamente su URL pública en este parámetro.'
                    : 'Sube aquí el PDF para guardar automáticamente su URL pública en este parámetro.'}
                </p>
                <div className="mt-4 space-y-3">
                  <input
                    type="file"
                    accept={editingParametro.slug === 'sistema_logo_url' || editingParametro.slug === 'logo_factura_url' ? 'image/png,image/jpeg,image/webp,image/svg+xml' : 'application/pdf'}
                    onChange={handleArchivoParametro}
                    className="block w-full text-sm text-stone-500 file:mr-3 file:rounded-2xl file:border-0 file:bg-blue-100 file:px-3 file:py-2 file:text-blue-700 hover:file:bg-blue-200"
                  />
                  {uploadingParametroArchivo && (
                    <p className="text-xs text-blue-700">
                      {editingParametro.slug === 'sistema_logo_url' || editingParametro.slug === 'logo_factura_url' ? 'Subiendo logo a MinIO...' : 'Subiendo PDF a MinIO...'}
                    </p>
                  )}
                  {parametroValor && (
                    editingParametro.slug === 'sistema_logo_url' || editingParametro.slug === 'logo_factura_url' ? (
                      <div className="space-y-3">
                        <img
                          src={parametroValor}
                          alt={editingParametro.slug === 'logo_factura_url' ? 'Logo de factura' : 'Logo del sistema'}
                          className="max-h-28 w-auto rounded-2xl border border-blue-100 bg-white p-3 shadow-sm"
                        />
                        <a href={parametroValor} target="_blank" rel="noreferrer" className="inline-flex text-sm font-medium text-blue-700 underline">
                          Abrir logo cargado
                        </a>
                      </div>
                    ) : (
                      <a href={parametroValor} target="_blank" rel="noreferrer" className="inline-flex text-sm font-medium text-blue-700 underline">
                        Ver PDF cargado
                      </a>
                    )
                  )}
                </div>
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-[var(--radius-card)] border border-stone-200 p-4 text-sm text-stone-600">
                <p><strong>Slug:</strong> {editingParametro.slug}</p>
                <p className="mt-2"><strong>Nombre:</strong> {editingParametro.nombre || '—'}</p>
                <p className="mt-2"><strong>Tipo:</strong> {editingParametro.tipo || '—'}</p>
              </div>
              <div className="rounded-[var(--radius-card)] border border-stone-200 p-4 text-sm text-stone-600">
                <p><strong>Visibilidad:</strong> {editingParametro.es_publico ? 'Público' : 'Privado'}</p>
                <p className="mt-2"><strong>Estado:</strong> {editingParametro.estado !== false ? 'Activo' : 'Inactivo'}</p>
                <p className="mt-2"><strong>Descripción:</strong> {editingParametro.descripcion || '—'}</p>
              </div>
            </div>

            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Valor</label>
              <textarea
                value={parametroValor}
                onChange={event => setParametroValor(event.target.value)}
                rows={6}
                className="ui-input"
              />
            </div>

            <div className="flex justify-end gap-3">
              <ModalCloseButton
                className="ui-btn ui-btn-secondary"
              >
                Cancelar
              </ModalCloseButton>
              <button
                type="button"
                onClick={() => void handleGuardarParametro()}
                disabled={savingParametro || uploadingParametroArchivo}
                className="ui-btn ui-btn-primary"
              >
                {savingParametro ? 'Guardando...' : 'Guardar valor'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
